
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, momentLocalizer, type DayLayoutFunction } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { EventInteractionArgs } from 'react-big-calendar/lib/addons/dragAndDrop';
import overlapLayout from 'react-big-calendar/lib/utils/layout-algorithms/overlap';
import moment from 'moment-timezone';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { HelpIcon } from '../components/HelpIcon';
import { useToast } from '../components/Toast';
import { apiClient } from '../utils/api';
import {
  clientNotesToPlainText,
  getClientNoteSections,
  isMinor as isMinorFromBirthDate,
  type ClientNoteSection,
} from '@digitaltattoo/shared-utils';
import { getAllActivities, PractitionerActivity, getActivityTypes, getEmployeeActivityTypes, ActivityType, EmployeeActivityType, updateActivity, deleteActivity, getActs, Act, createActivity } from '../services';
import { CalendarDragSidebar } from '../components/CalendarDragSidebar';
import { CalendarItemModal } from '../components/CalendarItemModal';
import { AppointmentCreatePage } from './AppointmentCreatePage';
import { useCurrentShop } from '../contexts/CurrentShopContext';
import {
  APPOINTMENT_STATUS_OPTIONS,
  STATUS_BADGE_CONFIG,
  appointmentStatusLabel,
} from '../components/appointment/appointmentConsultationUtils';
import { AppointmentStatusBadge } from '../components/appointment/AppointmentStatusBadge';
import { ConfirmModal } from '../components/ConfirmModal';
import { useRbac } from '../contexts/RbacContext';
import { Check, Clock3, Copy, Filter, Mail, PanelLeftClose, PanelLeftOpen, Plus, Store, X } from 'lucide-react';
import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { DateInput } from '../components/DateInput';
import { normalizeOpeningPeriods } from '../utils/openingHours';

const isFrenchLanguage = (language: string) =>
  String(language || '').toLowerCase().startsWith('fr');

// Function to configure moment locale based on language
const configureMomentLocale = (language: string) => {
  if (isFrenchLanguage(language)) {
    moment.updateLocale('fr', {
      // Keep Moment week calculations aligned with react-big-calendar:
      // French calendar weeks run from Monday through Sunday.
      week: {
        dow: 1,
        doy: 4,
      },
      months: [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
      ],
      monthsShort: [
        'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun',
        'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'
      ],
      weekdays: [
        'Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'
      ],
      weekdaysShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
      weekdaysMin: ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'],
      longDateFormat: {
        LT: 'HH:mm',
        LTS: 'HH:mm:ss',
        L: 'DD/MM/YYYY',
        LL: 'D MMMM YYYY',
        LLL: 'D MMMM YYYY HH:mm',
        LLLL: 'dddd D MMMM YYYY HH:mm'
      },
      calendar: {
        sameDay: '[Aujourd\'hui à] LT',
        nextDay: '[Demain à] LT',
        nextWeek: 'dddd [à] LT',
        lastDay: '[Hier à] LT',
        lastWeek: 'dddd [dernier à] LT',
        sameElse: 'L'
      },
      relativeTime: {
        future: 'dans %s',
        past: 'il y a %s',
        s: 'quelques secondes',
        m: 'une minute',
        mm: '%d minutes',
        h: 'une heure',
        hh: '%d heures',
        d: 'un jour',
        dd: '%d jours',
        M: 'un mois',
        MM: '%d mois',
        y: 'un an',
        yy: '%d ans'
      }
    });
    moment.locale('fr');
  } else {
    // English configuration
    moment.updateLocale('en-gb', {
      // The localizer below uses Sunday as the first day for non-French UI,
      // so Moment must use the same boundary for data loading.
      week: {
        dow: 0,
        doy: 6,
      },
      longDateFormat: {
        LT: 'HH:mm',
        LTS: 'HH:mm:ss',
        L: 'DD/MM/YYYY',
        LL: 'D MMMM YYYY',
        LLL: 'D MMMM YYYY HH:mm',
        LLLL: 'dddd D MMMM YYYY HH:mm'
      }
    });
    moment.locale('en-gb');
  }
};

// Create a dynamic localizer that adapts to the current language
const createLocalizer = (language: string) => {
  const localizer = momentLocalizer(moment);
  
  // Override the localizer's format functions to use current locale
  const originalFormat = localizer.format;
  localizer.format = (value: any, format: string, culture?: string) => {
    // Use current locale for formatting
    const currentLocale = isFrenchLanguage(language) ? 'fr' : 'en-gb';
    moment.locale(currentLocale);
    
    let result;
    if (format === 'MMMM YYYY') {
      // For month-year format like "October 2025"
      result = moment(value).format('MMMM YYYY');
    } else if (format === 'dddd') {
      // For day names
      result = moment(value).format('dddd');
    } else if (format === 'ddd') {
      // For short day names
      result = moment(value).format('ddd');
    } else if (format === 'HH:mm') {
      // For time format
      result = moment(value).format('HH:mm');
    } else if (format === 'MMM DD') {
      // For date range format
      result = moment(value).format('MMM DD');
    } else if (format === 'DD') {
      // For day number
      result = moment(value).format('DD');
    } else {
      result = originalFormat(value, format, culture);
    }
    
    return result;
  };

  // Set start of week based on locale (Monday for French, Sunday for English)
  localizer.startOfWeek = () => isFrenchLanguage(language) ? 1 : 0;
  
  return localizer;
};

// Compute week boundaries explicitly instead of relying on Moment's mutable locale.
// This keeps data loading aligned with react-big-calendar, especially for Sunday.
const getCalendarWeekStart = (value: any, language: string) => {
  const anchor = moment(value).startOf('day');
  const weekStartsOn = isFrenchLanguage(language) ? 1 : 0;
  return anchor.clone().subtract((anchor.day() - weekStartsOn + 7) % 7, 'days').startOf('day');
};

const getCalendarWeekEnd = (value: any, language: string) =>
  getCalendarWeekStart(value, language).add(7, 'days').subtract(1, 'millisecond');


// Function to create calendar messages based on current language
const createCalendarMessages = (t: any) => ({
  allDay: t('planning.calendar.calendarMessages.allDay'),
  previous: t('planning.calendar.calendarMessages.previous'),
  next: t('planning.calendar.calendarMessages.next'),
  today: t('planning.calendar.calendarMessages.today'),
  month: t('planning.calendar.calendarMessages.month'),
  week: t('planning.calendar.calendarMessages.week'),
  day: t('planning.calendar.calendarMessages.day'),
  agenda: t('planning.calendar.calendarMessages.agenda'),
  date: t('planning.calendar.calendarMessages.date'),
  time: t('planning.calendar.calendarMessages.time'),
  event: t('planning.calendar.calendarMessages.event'),
  noEventsInRange: t('planning.calendar.calendarMessages.noEventsInRange'),
  showMore: (total: number) => t('planning.calendar.calendarMessages.showMore', { total }),
  work_week: t('planning.calendar.calendarMessages.work_week'),
  yesterday: t('planning.calendar.calendarMessages.yesterday'),
  tomorrow: t('planning.calendar.calendarMessages.tomorrow'),
  am: t('planning.calendar.calendarMessages.am'),
  pm: t('planning.calendar.calendarMessages.pm'),
  AM: t('planning.calendar.calendarMessages.AM'),
  PM: t('planning.calendar.calendarMessages.PM'),
  monday: t('planning.calendar.calendarMessages.monday'),
  tuesday: t('planning.calendar.calendarMessages.tuesday'),
  wednesday: t('planning.calendar.calendarMessages.wednesday'),
  thursday: t('planning.calendar.calendarMessages.thursday'),
  friday: t('planning.calendar.calendarMessages.friday'),
  saturday: t('planning.calendar.calendarMessages.saturday'),
  sunday: t('planning.calendar.calendarMessages.sunday'),
  january: t('planning.calendar.calendarMessages.january'),
  february: t('planning.calendar.calendarMessages.february'),
  march: t('planning.calendar.calendarMessages.march'),
  april: t('planning.calendar.calendarMessages.april'),
  may: t('planning.calendar.calendarMessages.may'),
  june: t('planning.calendar.calendarMessages.june'),
  july: t('planning.calendar.calendarMessages.july'),
  august: t('planning.calendar.calendarMessages.august'),
  september: t('planning.calendar.calendarMessages.september'),
  october: t('planning.calendar.calendarMessages.october'),
  november: t('planning.calendar.calendarMessages.november'),
  december: t('planning.calendar.calendarMessages.december')
});

const DragAndDropCalendar = withDragAndDrop<Appointment, object>(Calendar as any);

// react-big-calendar dérive `minimumStartDifference` de `step * timeslots / 2`, soit 30 minutes
// avec notre grille (15 min x 4). Deux rendez-vous qui se suivent (ex. 17:00-17:15 puis 17:15)
// sont alors traités comme superposés et rendus en colonnes décalées. On force un seuil d'une
// minute pour ne colonner que les événements qui se chevauchent réellement dans le temps.
const dayLayoutAlgorithm: DayLayoutFunction<Appointment> = (params) =>
  overlapLayout({ ...params, minimumStartDifference: 1 });

interface Appointment {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  practitionerId?: string; // includes special "UNASSIGNED"
  employeeId?: string;
  practitionerName: string;
  clientName: string;
  clientIsMinor?: boolean | null;
  status: string;
  type: string;
  slotCount: number;
  timezone: string;
  shopId?: string | null;
  originalData?: any;
  isActivity?: boolean; // Flag to distinguish activities from appointments
  isTimeOff?: boolean;
  activityTypeName?: string;
  effectiveColor?: string;
}

const TIME_OFF_EVENT_COLOR = '#64748b';
const BACKOFFICE_EVENT_COLOR = '#b48449'; // Tailwind primary-500

function isApprovedTimeOff(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true' || value === 't';
}

function absenceAppliesToPractitioner(absence: any, practitioner: any): boolean {
  if (absence?.practitionerId) return absence.practitionerId === practitioner.id;
  if (absence?.employeeId) return false;
  return Boolean(
    absence?.requesterId &&
    (absence.requesterId === practitioner.id || absence.requesterId === practitioner.userId)
  );
}

const actStatusLabels: Record<string, string> = {
  PLANNED: 'Planifie',
  IN_PROGRESS: 'Pris en charge',
  COMPLETED: 'Termine',
  CANCELLED: 'Annule',
};

const actStatusBadgeClasses: Record<string, string> = {
  PLANNED: 'bg-blue-100 text-blue-800',
  IN_PROGRESS: 'bg-amber-100 text-amber-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-gray-100 text-gray-700',
};

const NON_CANCELLABLE_APPOINTMENT_STATUSES = new Set(['CANCELLED', 'COMPLETED', 'NO_SHOW', 'AWAITING_CHECKOUT']);

function isCancellableAppointment(event: Appointment | null | undefined): boolean {
  if (!event || event.isActivity || event.isTimeOff) return false;
  return !NON_CANCELLABLE_APPOINTMENT_STATUSES.has(String(event.status || '').toUpperCase());
}

function getAppointmentClientEmail(event: Appointment | null | undefined): string {
  const original = event?.originalData || {};
  return String(original.clientEmail || original.clientemail || original.client?.email || '').trim();
}

function getAppointmentClientPhone(event: Appointment | null | undefined): string {
  const original = event?.originalData || {};
  return String(original.clientPhone || original.clientphone || original.client?.phone || '').trim();
}

function parseMaybeJson(value: unknown): any {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

// "Zone - Type" pairs for a piercing appointment. `pricing.piercingTypes` carries the
// denormalized names; client notes are the fallback for older appointments.
function getAppointmentPiercingSummary(event: Appointment | null | undefined): string {
  const original = event?.originalData || {};
  const pricing = parseMaybeJson(original.pricing);
  const pricingEntries = Array.isArray(pricing?.piercingTypes)
    ? pricing.piercingTypes
    : pricing?.piercingType
      ? [pricing.piercingType]
      : [];

  const entries: any[] = pricingEntries.length
    ? pricingEntries
    : getClientNoteSections(original.clientNotes).filter((section) => section.kind === 'piercing');

  const labels = entries
    .map((entry) => {
      const zone = String(entry?.bodyZone || entry?.zone || '').trim();
      const type = String(entry?.name || entry?.piercingType || '').trim();
      return [zone, type].filter(Boolean).join(' - ');
    })
    .filter(Boolean);

  return Array.from(new Set(labels)).join(', ');
}

function hasPendingRescheduleEmail(event: Appointment | null | undefined): boolean {
  const value = event?.originalData?.rescheduleNotificationPending
    ?? event?.originalData?.reschedulenotificationpending;
  return value === true || value === 1 || value === '1' || value === 'true' || value === 't';
}

async function copyTextToClipboard(text: string): Promise<boolean> {
  const value = String(text || '').trim();
  if (!value) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Fall through to the execCommand fallback below.
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  } catch {
    return false;
  }
}

function isVisibleOnCalendar(event: { status?: string; isActivity?: boolean; isTimeOff?: boolean } | null | undefined): boolean {
  if (!event || event.isActivity || event.isTimeOff) return true;
  return String(event.status || '').toUpperCase() !== 'CANCELLED';
}

interface CalendarView {
  type: 'shop' | 'company';
  id: string;
  name: string;
}

interface OpeningHours {
  monday?: { open: string; close: string } | Array<{ open: string; close: string }>;
  tuesday?: { open: string; close: string } | Array<{ open: string; close: string }>;
  wednesday?: { open: string; close: string } | Array<{ open: string; close: string }>;
  thursday?: { open: string; close: string } | Array<{ open: string; close: string }>;
  friday?: { open: string; close: string } | Array<{ open: string; close: string }>;
  saturday?: { open: string; close: string } | Array<{ open: string; close: string }>;
  sunday?: { open: string; close: string } | Array<{ open: string; close: string }>;
}

const OPENING_HOUR_DAY_KEYS: Array<keyof OpeningHours> = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

interface CalendarSettings {
  calendarStartHour: number;
  calendarEndHour: number;
  calendarStepMinutes: number;
}

const ALL_SHOPS_ID = '__ALL_SHOPS__';
const NO_SHOP_ID = '__NO_SHOP__';
const MOBILE_CALENDAR_QUERY = '(max-width: 1023px)';
const SLOT_TIME_ATTRIBUTE = 'data-slot-time';
const SLOT_DATE_ATTRIBUTE = 'data-slot-date';
const DAY_CELL_DEFAULT_HOUR = 9;

function getIsMobileCalendar(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(MOBILE_CALENDAR_QUERY).matches;
}

// Month and all-day cells only carry a day, so start them at the first business hour.
function normalizeDayCellStart(value: Date): Date {
  return value.getHours() === 0 && value.getMinutes() === 0
    ? moment(value).hour(DAY_CELL_DEFAULT_HOUR).toDate()
    : value;
}

function readSlotDate(element: Element | null | undefined, attribute: string): Date | null {
  const rawValue = element?.getAttribute(attribute);
  if (!rawValue) return null;
  const parsed = new Date(rawValue);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function findSlotDateUnderPointer(event: React.MouseEvent, attribute: string): Date | null {
  const target = event.target as Element | null;
  const fromTarget = readSlotDate(target?.closest(`[${attribute}]`), attribute);
  if (fromTarget) return fromTarget;

  // Event containers are absolutely positioned on top of the background slots, so a click
  // usually lands on the overlay rather than on the slot it visually points at.
  if (typeof document === 'undefined') return null;
  for (const element of document.elementsFromPoint(event.clientX, event.clientY)) {
    const fromStack = readSlotDate(element.closest(`[${attribute}]`), attribute);
    if (fromStack) return fromStack;
  }
  return null;
}


type CalendarPresencePerson = {
  id: string;
  firstName: string;
  fullName: string;
  windows: Array<[number, number]>;
};

type CalendarPresenceState = {
  key: string;
  loading: boolean;
  error: boolean;
  byDay: Record<string, CalendarPresencePerson[]>;
};

type ShopAvailabilityPerson = {
  id: string;
  name: string;
  windows: Array<[number, number]>;
};

type ShopAvailabilityState = {
  key: string;
  loading: boolean;
  error: string | null;
  people: ShopAvailabilityPerson[];
  unknown: number;
  // Praticiens planifiés ce jour-là mais dont la journée est entièrement occupée.
  busy: number;
  timezone: string;
  openingIntervals: Array<[number, number]>;
};

const availabilityDayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function subtractIntervals(base: Array<[number, number]>, blocked: Array<[number, number]>): Array<[number, number]> {
  return blocked.reduce((remaining, [blockedFrom, blockedTo]) => {
    if (!Number.isFinite(blockedFrom) || !Number.isFinite(blockedTo) || blockedFrom >= blockedTo) return remaining;
    return remaining.flatMap(([from, to]): Array<[number, number]> => {
      if (blockedTo <= from || blockedFrom >= to) return [[from, to]];
      const next: Array<[number, number]> = [];
      if (from < blockedFrom) next.push([from, Math.min(to, blockedFrom)]);
      if (blockedTo < to) next.push([Math.max(from, blockedTo), to]);
      return next;
    });
  }, base);
}

function intersectIntervals(left: Array<[number, number]>, right: Array<[number, number]>): Array<[number, number]> {
  if (!right.length) return left;
  const result: Array<[number, number]> = [];
  for (const [leftFrom, leftTo] of left) {
    for (const [rightFrom, rightTo] of right) {
      const from = Math.max(leftFrom, rightFrom);
      const to = Math.min(leftTo, rightTo);
      if (from < to) result.push([from, to]);
    }
  }
  return result;
}

function normalizeScheduleSlots(value: unknown): Array<{ open: string; close: string }> {
  const rawSlots = Array.isArray(value) ? value : value ? [value] : [];

  return rawSlots.filter((slot): slot is { open: string; close: string } =>
    !!slot &&
    typeof slot === 'object' &&
    /^\d{2}:\d{2}$/.test(String((slot as any).open || '')) &&
    /^\d{2}:\d{2}$/.test(String((slot as any).close || ''))
  );
}

function getPractitionerScheduleForShop(practitioner: any, shopId?: string): Record<string, any> | null {
  let availability = practitioner?.availability;
  if (typeof availability === 'string') {
    try { availability = JSON.parse(availability); } catch { availability = null; }
  }
  if (!availability || typeof availability !== 'object') return null;

  let shopSchedules = availability.shopSchedules;
  if (typeof shopSchedules === 'string') {
    try { shopSchedules = JSON.parse(shopSchedules); } catch { shopSchedules = null; }
  }

  const hasShopSchedules =
    shopSchedules &&
    typeof shopSchedules === 'object' &&
    !Array.isArray(shopSchedules) &&
    Object.keys(shopSchedules).length > 0;

  // Dès qu'un planning par shop existe, le shop sélectionné devient la source de vérité.
  // On ne retombe pas sur le planning global si ce shop n'a pas de planning configuré,
  // sinon les horaires d'un autre shop pourraient apparaître par erreur.
  if (shopId && hasShopSchedules) {
    let shopSchedule = shopSchedules[shopId];
    if (typeof shopSchedule === 'string') {
      try { shopSchedule = JSON.parse(shopSchedule); } catch { shopSchedule = null; }
    }

    return shopSchedule && typeof shopSchedule === 'object' && Object.keys(shopSchedule).length > 0
      ? shopSchedule
      : null;
  }

  // Compatibilité avec les anciennes données tant qu'aucun shopSchedules n'existe.
  let legacySchedule = availability.schedule;
  if (typeof legacySchedule === 'string') {
    try { legacySchedule = JSON.parse(legacySchedule); } catch { legacySchedule = null; }
  }

  return legacySchedule && typeof legacySchedule === 'object' && Object.keys(legacySchedule).length > 0
    ? legacySchedule
    : null;
}

function getAvailabilityIntervals(practitioner: any, day: string, timezone: string, shopId?: string): Array<[number, number]> | null {
  const start = moment.tz(day, timezone).startOf('day');
  const end = start.clone().add(1, 'day');
  const schedule = getPractitionerScheduleForShop(practitioner, shopId);
  if (!schedule) return null;

  const intervals: Array<[number, number]> = [];
  for (const date of [start.clone().subtract(1, 'day'), start]) {
    const dateStr = date.format('YYYY-MM-DD');
    const slots = normalizeScheduleSlots(schedule[availabilityDayNames[date.day()]]);

    for (const slot of slots) {
      const from = moment.tz(`${dateStr} ${slot.open}`, 'YYYY-MM-DD HH:mm', true, timezone);
      const to = moment.tz(`${dateStr} ${slot.close}`, 'YYYY-MM-DD HH:mm', true, timezone);
      if (!from.isValid() || !to.isValid() || slot.open === slot.close) continue;
      if (to.isBefore(from)) to.add(1, 'day');
      const interval: [number, number] = [Math.max(from.valueOf(), start.valueOf()), Math.min(to.valueOf(), end.valueOf())];
      if (interval[0] < interval[1]) intervals.push(interval);
    }
  }
  return intervals.sort((left, right) => left[0] - right[0]);
}

function getShopOpeningIntervals(openingHours: OpeningHours | undefined, day: string, timezone: string): Array<[number, number]> {
  if (!openingHours || typeof openingHours !== 'object') return [];
  const start = moment.tz(day, timezone).startOf('day');
  const end = start.clone().add(1, 'day');
  const intervals: Array<[number, number]> = [];
  for (const date of [start.clone().subtract(1, 'day'), start]) {
    const slots = normalizeScheduleSlots(openingHours[availabilityDayNames[date.day()] as keyof OpeningHours]);
    for (const slot of slots) {
      const from = moment.tz(`${date.format('YYYY-MM-DD')} ${slot.open}`, 'YYYY-MM-DD HH:mm', true, timezone);
      const to = moment.tz(`${date.format('YYYY-MM-DD')} ${slot.close}`, 'YYYY-MM-DD HH:mm', true, timezone);
      if (!from.isValid() || !to.isValid() || slot.open === slot.close) continue;
      if (to.isBefore(from)) to.add(1, 'day');
      const interval: [number, number] = [Math.max(from.valueOf(), start.valueOf()), Math.min(to.valueOf(), end.valueOf())];
      if (interval[0] < interval[1]) intervals.push(interval);
    }
  }
  return intervals;
}

// Les endpoints de planning renvoient soit un instant absolu (ISO/UTC), soit une heure déjà
// convertie dans le fuseau du shop sous forme "YYYY-MM-DD HH:mm:ss" sans décalage. Lire les
// deux formats évite de décaler les créneaux occupés quand le shop consulté n'est pas dans
// le fuseau du navigateur.
function parseShopTimestamp(value: unknown, timezone: string): number {
  const raw = String(value ?? '').trim();
  if (!raw) return NaN;
  const isAbsolute = /(?:Z|[+-]\d{2}:?\d{2})$/.test(raw);
  const parsed = isAbsolute
    ? moment.parseZone(raw)
    : moment.tz(raw.replace(' ', 'T'), timezone);
  return parsed.isValid() ? parsed.valueOf() : NaN;
}

// Les bornes illisibles ressortent en NaN : subtractIntervals ignore déjà ces intervalles.
function toBlockingInterval(start: unknown, end: unknown, timezone: string): [number, number] {
  return [parseShopTimestamp(start, timezone), parseShopTimestamp(end, timezone)];
}

function formatFreeDuration(totalMinutes: number): string {
  const minutes = Math.max(0, Math.round(totalMinutes));
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (!hours) return `${remainder} min`;
  return remainder ? `${hours} h ${remainder} min` : `${hours} h`;
}

function normalizeFreeWindows(intervals: Array<[number, number]>, day: string, timezone: string): Array<[number, number]> {
  const step = 15 * 60 * 1000;
  const now = moment.tz(timezone);
  const isToday = now.format('YYYY-MM-DD') === day;
  const nowRounded = Math.ceil(now.valueOf() / step) * step;
  return intervals
    .map(([from, to]): [number, number] => {
      const clippedFrom = isToday ? Math.max(from, nowRounded) : from;
      return [Math.ceil(clippedFrom / step) * step, Math.floor(to / step) * step];
    })
    .filter(([from, to]) => to - from >= step)
    .sort((a, b) => a[0] - b[0]);
}

// Présence réelle = planning hebdomadaire du praticien pour le shop affiché.
// Un jour absent du planning signifie que le praticien n'est pas présent ce jour-là.
function getShopPresenceForDay(
  practitioners: any[],
  absences: any[],
  day: string,
  timezone: string,
  shopId: string,
) {
  const start = moment.tz(day, timezone).startOf('day');
  const end = start.clone().add(1, 'day');
  let unknown = 0;
  const people: CalendarPresencePerson[] = [];

  for (const practitioner of practitioners) {
    const schedule = getPractitionerScheduleForShop(practitioner, shopId);
    if (!schedule) {
      unknown += 1;
      continue;
    }

    let intervals: Array<[number, number]> = [];
    // Include shifts starting yesterday and continuing past midnight.
    for (const date of [start.clone().subtract(1, 'day'), start]) {
      const dateStr = date.format('YYYY-MM-DD');
      const slots = normalizeScheduleSlots(schedule[availabilityDayNames[date.day()]]);

      for (const slot of slots) {
        const from = moment.tz(`${dateStr} ${slot.open}`, 'YYYY-MM-DD HH:mm', true, timezone);
        const to = moment.tz(`${dateStr} ${slot.close}`, 'YYYY-MM-DD HH:mm', true, timezone);
        if (!from.isValid() || !to.isValid() || slot.open === slot.close) continue;
        if (to.isBefore(from)) to.add(1, 'day');

        const interval: [number, number] = [
          Math.max(from.valueOf(), start.valueOf()),
          Math.min(to.valueOf(), end.valueOf()),
        ];
        if (interval[0] < interval[1]) intervals.push(interval);
      }
    }

    for (const absence of absences) {
      if (!absenceAppliesToPractitioner(absence, practitioner) || !isApprovedTimeOff(absence.isApproved)) continue;
      const absenceTimezone = moment.tz.zone(absence.timezone || '') ? absence.timezone : timezone;
      const from = parseShopTimestamp(absence.startDateTime, absenceTimezone);
      const to = parseShopTimestamp(absence.endDateTime, absenceTimezone);
      intervals = subtractIntervals(intervals, [[from, to]]);
    }

    if (!intervals.length) continue;

    const fullName =
      [practitioner.firstName, practitioner.lastName].filter(Boolean).join(' ') ||
      practitioner.artistName ||
      'Praticien';
    const firstName =
      String(practitioner.firstName || practitioner.artistName || fullName)
        .trim()
        .split(/\s+/)[0] || 'Praticien';

    people.push({
      id: practitioner.id,
      firstName,
      fullName,
      windows: intervals.sort((left, right) => left[0] - right[0]),
    });
  }

  return { people, unknown };
}


export function PlanningCalendarPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { showToast, ToastComponent } = useToast();
  const { can } = useRbac();
  const { currentShopId: globalCurrentShopId, setCurrentShopId, shops: contextShops, currentShop: contextCurrentShop } =
    useCurrentShop();
  const userStr = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
  const currentUser = userStr ? JSON.parse(userStr) : null;
  const isPractitionerUser = currentUser?.userType === 'PRACTITIONER';
  const userRole = String(currentUser?.role || '').toLowerCase().replace(/-/g, '_');
  const isManager = userRole === 'manager';
  const canViewShopPresence = ['manager', 'shop_admin', 'company_admin', 'admin', 'super_admin'].includes(userRole);
  const canCancelAppointments =
    can('appointments:cancel') ||
    ['manager', 'shop_admin', 'company_admin', 'admin', 'super_admin'].includes(userRole);
  const ownPractitionerId = isPractitionerUser ? currentUser?.id : null;
  const [currentView, setCurrentView] = useState<CalendarView>({ type: 'shop', id: '', name: '' });
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewScope, setViewScope] = useState<'all' | 'mine'>(isPractitionerUser ? 'mine' : 'all');
  const [calendarType, setCalendarType] = useState<'practitioner' | 'tattoo' | 'piercer' | 'employee'>('practitioner');
  const [calendarSettings, setCalendarSettings] = useState<CalendarSettings>({
    calendarStartHour: 7,
    calendarEndHour: 22,
    calendarStepMinutes: 15,
  });
  
  const [isMobile, setIsMobile] = useState(getIsMobileCalendar);
  const [showFiltersPopup, setShowFiltersPopup] = useState(false);
  // Default calendar view is day on mobile, current week on desktop
  const [calendarView, setCalendarView] = useState<'month' | 'week' | 'day'>(
    getIsMobileCalendar() ? 'day' : 'week'
  );
  const [shops, setShops] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [allCompanies, setAllCompanies] = useState<any[]>([]); // Store all companies before filtering
  const [loadingShops, setLoadingShops] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState<Appointment | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingAppointment, setCancellingAppointment] = useState(false);
  const [appointmentActs, setAppointmentActs] = useState<Act[]>([]);
  const [loadingAppointmentActs, setLoadingAppointmentActs] = useState(false);
  const [isMovingEvent, setIsMovingEvent] = useState(false);
  const [sendingRescheduleEmailId, setSendingRescheduleEmailId] = useState<string | null>(null);
  const [serviceNameMap, setServiceNameMap] = useState<Record<string, string>>({});
  const servicesLoadedRef = useRef(false);
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    x: number;
    y: number;
    start: Date;
    end: Date;
  } | null>(null);
  const [closedSlotNotice, setClosedSlotNotice] = useState<{ start: Date; end: Date } | null>(null);
  const [eventContextMenu, setEventContextMenu] = useState<{
    isOpen: boolean;
    x: number;
    y: number;
    event: Appointment;
  } | null>(null);
  const [showPractitionerDropdown, setShowPractitionerDropdown] = useState(false);
  const [practitionerHover, setPractitionerHover] = useState<{ text: string; x: number; y: number; personId: string } | null>(null);
  const practitionerHoverDismissedIdRef = useRef<string | null>(null);
  const practitionerHoverRef = useRef(practitionerHover);
  practitionerHoverRef.current = practitionerHover;
  const [eventHover, setEventHover] = useState<{ text: string; x: number; y: number; anchorTop: number } | null>(null);
  const eventHoverRef = useRef<HTMLDivElement | null>(null);
  const [eventHoverCopied, setEventHoverCopied] = useState(false);
  const eventHoverHideTimerRef = useRef<number | null>(null);
  // Hidden by default so the calendar uses the full width; toggled from the header.
  const [showLeftSidebar, setShowLeftSidebar] = useState(false);
  const [showFiltersPanel, setShowFiltersPanel] = useState(true);
  const practitionerDropdownRef = useRef<HTMLDivElement | null>(null);
  const ignoreNextSlotClickRef = useRef(false);
  const [isDragModifierPressed, setIsDragModifierPressed] = useState(false);
  const isDragModifierPressedRef = useRef(false);
  
  // Date range filter state - default to current week
  const [useCustomDateRange, setUseCustomDateRange] = useState(false);
  const [fromDate, setFromDate] = useState(() => getCalendarWeekStart(moment(), i18n.language).format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(() => getCalendarWeekEnd(moment(), i18n.language).format('YYYY-MM-DD'));
  
  // Practitioner filter state
  const [practitioners, setPractitioners] = useState<any[]>([]);
  const [currentShopPractitionerIds, setCurrentShopPractitionerIds] = useState<Set<string> | null>(null);
  const [currentShopPractitioners, setCurrentShopPractitioners] = useState<{
    shopId: string;
    practitioners: any[];
  } | null>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [selectedPractitionerIds, setSelectedPractitionerIds] = useState<string[]>([]);
  const [loadingPractitioners, setLoadingPractitioners] = useState(false);
  // Appointment status filter (applies to appointments in all calendar views)
  const [statusFilter, setStatusFilter] = useState('');
  const [allAppointments, setAllAppointments] = useState<Appointment[]>([]); // Store all appointments before filtering
  const [activities, setActivities] = useState<PractitionerActivity[]>([]);
  const [timeOffs, setTimeOffs] = useState<any[]>([]);

  useEffect(() => () => {
    if (eventHoverHideTimerRef.current) {
      window.clearTimeout(eventHoverHideTimerRef.current);
    }
  }, []);

useEffect(() => {
  const shopId =
    currentView.type === 'shop' &&
    currentView.id &&
    currentView.id !== ALL_SHOPS_ID &&
    currentView.id !== NO_SHOP_ID
      ? currentView.id
      : null;

  if (!shopId) {
    setCurrentShopPractitionerIds(null);
    setCurrentShopPractitioners(null);
    return;
  }

  const controller = new AbortController();
  setCurrentShopPractitioners(null);

  const loadCurrentShopPractitioners = async () => {
    try {
      const response = await apiClient(
        `/api/planning/practitioners/shop/${encodeURIComponent(shopId)}`,
        { signal: controller.signal }
      );

      const body = await response.json().catch(() => ({}));

      if (!response.ok || !body?.success) {
        throw new Error('Impossible de charger les praticiens du shop');
      }

      const shopPractitioners = Array.isArray(body.data?.practitioners)
        ? body.data.practitioners
        : [];

      const practitionerIdsForShop = shopPractitioners
        .filter(
          (practitioner: any) =>
            getPractitionerScheduleForShop(practitioner, shopId) !== null
        )
        .map((practitioner: any) => practitioner.id);

      setCurrentShopPractitionerIds(
        new Set(practitionerIdsForShop)
      );
      setCurrentShopPractitioners({
        shopId,
        practitioners: shopPractitioners,
      });
    } catch (error) {
      if (controller.signal.aborted) return;

      console.error(
        'Failed to load practitioners for current shop:',
        error
      );

      setCurrentShopPractitionerIds(new Set());
      setCurrentShopPractitioners({
        shopId,
        practitioners: [],
      });
    }
  };

  void loadCurrentShopPractitioners();

  return () => controller.abort();
}, [currentView.type, currentView.id]);


  const [calendarPresence, setCalendarPresence] = useState<CalendarPresenceState>({
    key: '',
    loading: false,
    error: false,
    byDay: {},
  });
  const [showOtherShopAvailability, setShowOtherShopAvailability] = useState(false);
  const [availabilityShopId, setAvailabilityShopId] = useState('');
  const [availabilityDate, setAvailabilityDate] = useState(() => moment().format('YYYY-MM-DD'));
  const [availabilityShops, setAvailabilityShops] = useState<any[]>([]);
  const [loadingAvailabilityShops, setLoadingAvailabilityShops] = useState(false);
  const [shopAvailability, setShopAvailability] = useState<ShopAvailabilityState>({
    key: '', loading: false, error: null, people: [], unknown: 0, busy: 0,
    timezone: 'Europe/Paris', openingIntervals: [],
  });
  const presenceShopId = currentView.type === 'shop' && currentView.id !== ALL_SHOPS_ID && currentView.id !== NO_SHOP_ID
    ? currentView.id : '';
  const presenceShop = shops.find((shop) => shop.id === presenceShopId);
  const presenceTimezone = moment.tz.zone(presenceShop?.timezone || '') ? presenceShop.timezone : 'Europe/Paris';

  useEffect(() => {
    if (!showOtherShopAvailability) return;

    const controller = new AbortController();
    setLoadingAvailabilityShops(true);

    // Endpoint dédié en lecture seule : il renvoie tous les shops actifs du réseau, alors que
    // /api/admin/shops reste limité aux shops assignés au compte.
    apiClient('/api/planning/shop-availability/shops', { signal: controller.signal })
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok || !body?.success) {
          throw new Error(body?.message || 'Impossible de charger la liste des shops.');
        }

        const completeShops = (Array.isArray(body.data?.shops) ? body.data.shops : [])
          .map((shop: any) => ({
            id: shop.id,
            name: shop.name,
            companyId: shop.companyId,
            companyName: shop.companyName,
            timezone: shop.timezone || 'Europe/Paris',
            openingHours: shop.openingHours || {},
          }))
          .sort((a: any, b: any) =>
            String(a.name || '').localeCompare(String(b.name || ''), 'fr', { sensitivity: 'base' })
          );

        if (!controller.signal.aborted) setAvailabilityShops(completeShops);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.error('Failed to load all shops for availability:', error);
        setAvailabilityShops([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingAvailabilityShops(false);
      });

    return () => controller.abort();
  }, [showOtherShopAvailability]);

  const allAvailabilityShops = useMemo(() => {
    const byId = new Map<string, any>();

    const addShops = (items: any[]) => {
      for (const rawShop of Array.isArray(items) ? items : []) {
        const id = String(rawShop?.id || rawShop?.shopId || '').trim();
        if (!id || id === ALL_SHOPS_ID || id === NO_SHOP_ID) continue;

        const previous = byId.get(id) || {};
        byId.set(id, {
          ...previous,
          ...rawShop,
          id,
          name: rawShop?.name || rawShop?.shopName || previous.name || id,
          companyId: rawShop?.companyId || rawShop?.company?.id || previous.companyId,
          companyName: rawShop?.companyName || rawShop?.company?.name || previous.companyName,
          timezone: rawShop?.timezone || previous.timezone || 'Europe/Paris',
          openingHours: rawShop?.openingHours || previous.openingHours || {},
        });
      }
    };

    // The header context is the most reliable list of shops the logged-in user can access.
    addShops(contextShops as any[]);
    addShops(shops);
    addShops(availabilityShops);

    return Array.from(byId.values()).sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), 'fr', { sensitivity: 'base' })
    );
  }, [contextShops, shops, availabilityShops]);

  const otherShops = useMemo(() => {
    const currentShopToExclude = presenceShopId || (
      globalCurrentShopId && globalCurrentShopId !== ALL_SHOPS_ID && globalCurrentShopId !== NO_SHOP_ID
        ? globalCurrentShopId
        : ''
    );
    return allAvailabilityShops.filter((shop) => shop.id !== currentShopToExclude);
  }, [allAvailabilityShops, presenceShopId, globalCurrentShopId]);

  // La consultation part toujours du jour affiché dans le calendrier : on évite à l'utilisateur
  // de re-saisir la date qu'il regarde déjà.
  const openOtherShopAvailability = useCallback(() => {
    setAvailabilityDate(moment(selectedDate).format('YYYY-MM-DD'));
    setShowOtherShopAvailability(true);
  }, [selectedDate]);

  const shiftAvailabilityDate = useCallback((days: number) => {
    setAvailabilityDate((previous) =>
      moment(previous, 'YYYY-MM-DD').add(days, 'days').format('YYYY-MM-DD')
    );
  }, []);

  // Un seul autre shop accessible : on le sélectionne d'office. Sinon on repart d'une sélection
  // vide dès que le shop mémorisé n'est plus dans la liste (changement de shop courant).
  useEffect(() => {
    if (!showOtherShopAvailability || loadingAvailabilityShops) return;
    if (availabilityShopId && otherShops.some((shop) => shop.id === availabilityShopId)) return;
    setAvailabilityShopId(otherShops.length === 1 ? otherShops[0].id : '');
  }, [showOtherShopAvailability, loadingAvailabilityShops, otherShops, availabilityShopId]);

  useEffect(() => {
    if (!showOtherShopAvailability || !availabilityShopId || !availabilityDate) return;
    const selectedShop = allAvailabilityShops.find((shop) => shop.id === availabilityShopId);
    if (!selectedShop) return;

    const timezone = moment.tz.zone(selectedShop.timezone || '') ? selectedShop.timezone : 'Europe/Paris';
    const start = moment.tz(availabilityDate, timezone).startOf('day');
    const end = start.clone().add(1, 'day');
    const requestKey = `${availabilityShopId}:${availabilityDate}:${timezone}`;
    const controller = new AbortController();
    setShopAvailability({
      key: requestKey, loading: true, error: null, people: [], unknown: 0, busy: 0,
      timezone, openingIntervals: [],
    });

    const read = async (url: string) => {
      const response = await apiClient(url, { signal: controller.signal });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body?.success) throw new Error(body?.message || 'Impossible de charger les disponibilités.');
      return body.data;
    };

    const availabilityParams = new URLSearchParams({
      shopId: availabilityShopId,
      from: start.toISOString(),
      to: end.toISOString(),
    });

    read(`/api/planning/shop-availability?${availabilityParams.toString()}`).then((payload) => {
      if (controller.signal.aborted) return;
      const practitionersForShop = Array.isArray(payload?.practitioners) ? payload.practitioners : [];

      // Le back renvoie déjà les périodes bloquantes normalisées (absences approuvées,
      // rendez-vous actifs, activités bloquantes) sans aucune donnée client.
      const busyByPractitioner = new Map<string, Array<[number, number]>>();
      for (const period of Array.isArray(payload?.busy) ? payload.busy : []) {
        const practitionerId = String(period?.practitionerId || '');
        if (!practitionerId) continue;
        const blocks = busyByPractitioner.get(practitionerId) || [];
        blocks.push(toBlockingInterval(period.start, period.end, timezone));
        busyByPractitioner.set(practitionerId, blocks);
      }

      const shopOpeningIntervals = getShopOpeningIntervals(
        payload?.shop?.openingHours || selectedShop.openingHours,
        availabilityDate,
        timezone,
      );
      let unknown = 0;
      let busy = 0;
      const people: ShopAvailabilityPerson[] = [];

      for (const practitioner of practitionersForShop) {
        const baseAvailability = getAvailabilityIntervals(practitioner, availabilityDate, timezone, availabilityShopId);
        if (baseAvailability === null) {
          unknown += 1;
          continue;
        }
        if (!baseAvailability.length) continue;

        let free = shopOpeningIntervals.length
          ? intersectIntervals(baseAvailability, shopOpeningIntervals)
          : baseAvailability;

        free = subtractIntervals(free, busyByPractitioner.get(practitioner.id) || []);

        const windows = normalizeFreeWindows(free, availabilityDate, timezone);
        if (!windows.length) {
          busy += 1;
          continue;
        }
        people.push({
          id: practitioner.id,
          name: [practitioner.firstName, practitioner.lastName].filter(Boolean).join(' ') || practitioner.artistName || 'Praticien',
          windows,
        });
      }

      people.sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }));
      setShopAvailability({
        key: requestKey, loading: false, error: null, people, unknown, busy,
        timezone, openingIntervals: shopOpeningIntervals,
      });
    }).catch((error) => {
      if (controller.signal.aborted) return;
      setShopAvailability({
        key: requestKey,
        loading: false,
        error: error instanceof Error ? error.message : 'Impossible de charger les disponibilités.',
        people: [],
        unknown: 0,
        busy: 0,
        timezone,
        openingIntervals: [],
      });
    });

    return () => controller.abort();
  }, [showOtherShopAvailability, availabilityShopId, availabilityDate, allAvailabilityShops]);

  useEffect(() => {
    if (!showOtherShopAvailability) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowOtherShopAvailability(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showOtherShopAvailability]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_CALENDAR_QUERY);
    const onChange = () => {
      setIsMobile(mediaQuery.matches);
      if (!mediaQuery.matches) {
        setShowFiltersPopup(false);
      }
    };
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!isMobile || !showFiltersPopup) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowFiltersPopup(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMobile, showFiltersPopup]);


  useEffect(() => {
    if (viewScope !== 'mine' || !ownPractitionerId) {
      return;
    }
    setSelectedPractitionerIds((prev) => {
      if (prev.length === 1 && prev[0] === ownPractitionerId) {
        return prev;
      }
      return [ownPractitionerId];
    });
  }, [viewScope, ownPractitionerId]);

  // Drag and drop state
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [employeeActivityTypes, setEmployeeActivityTypes] = useState<EmployeeActivityType[]>([]);
  const [draggedItem, setDraggedItem] = useState<any>(null);
  const draggedItemRef = useRef<any>(null); // Use ref to persist across renders
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalData, setCreateModalData] = useState<{
    type: 'activity' | 'appointment';
    start: Date;
    end: Date;
    activityTypeId?: string;
    employeeActivityTypeId?: string;
    appointmentType?: string;
    activityId?: string;
    appointmentId?: string;
    shopId?: string;
    practitionerId?: string | null;
    employeeId?: string | null;
    clientId?: string;
    title?: string;
    description?: string;
    isAllDay?: boolean;
    calendarTypeOverride?: 'practitioner' | 'employee';
  } | null>(null);
  
  // Calendar time bounds based on opening hours
  const [calendarMinTime, setCalendarMinTime] = useState(new Date(2000, 0, 1, 7, 0, 0)); // Default: 7:00 AM
  const [calendarMaxTime, setCalendarMaxTime] = useState(new Date(2000, 0, 1, 22, 0, 0)); // Default: 10:00 PM
  // Removed unused currentOpeningHours state

  // Create dynamic localizer and messages based on current language
  const currentLanguage = i18n.language;
  const localizer = useMemo(
    () => createLocalizer(currentLanguage),
    [currentLanguage],
  );
  const messages = useMemo(
    () => createCalendarMessages(t),
    [t, currentLanguage],
  );
  const getPractitionersForType = useCallback(
    (type: 'practitioner' | 'tattoo' | 'piercer' | 'employee') => {
      if (type === 'employee') {
        return employees.map((employee) => ({
          id: employee.id,
          fullName: employee.fullName,
          shopIds: employee.shopIds,
        }));
      }
      if (type === 'practitioner') {
        return practitioners;
      }
      const requiredSpecialty = type === 'tattoo' ? 'TATTOO' : 'PIERCING';
      return practitioners.filter((practitioner) => {
        const specialties = (practitioner.specialties || [])
          .map((value: any) => (typeof value === 'string' ? value : String(value)))
          .map((value: string) => value.toUpperCase());
        return specialties.includes(requiredSpecialty);
      });
    },
    [employees, practitioners]
  );

  const filteredPractitioners = useMemo(
    () => getPractitionersForType(calendarType),
    [calendarType, getPractitionersForType]
  );

  const filteredPractitionerIdSet = useMemo(
    () => new Set(filteredPractitioners.map((practitioner) => practitioner.id)),
    [filteredPractitioners]
  );


  const legendPractitioners = useMemo(() => {
  if (!currentShopPractitionerIds) {
    return filteredPractitioners;
  }

  return filteredPractitioners.filter(
    (practitioner) =>
      practitioner.id === 'UNASSIGNED' ||
      currentShopPractitionerIds.has(practitioner.id)
  );
}, [filteredPractitioners, currentShopPractitionerIds]);

  const effectivePractitionerIds = useMemo(() => {
    const baseIds = viewScope === 'mine' && ownPractitionerId
      ? [ownPractitionerId]
      : selectedPractitionerIds;
    return baseIds.filter((id) => filteredPractitionerIdSet.has(id));
  }, [viewScope, ownPractitionerId, selectedPractitionerIds, filteredPractitionerIdSet]);

  const effectivePractitionerIdsKey = useMemo(
    () => effectivePractitionerIds.join(','),
    [effectivePractitionerIds]
  );

  const calendarPresenceRange = useMemo(() => {
    const selectedDay = moment(selectedDate).format('YYYY-MM-DD');
    const anchorDate = moment.tz(selectedDay, presenceTimezone).startOf('day');
    const weekStartsOn = isFrenchLanguage(currentLanguage) ? 1 : 0;
    const alignToWeekStart = (value: moment.Moment) =>
      value.clone().subtract((value.day() - weekStartsOn + 7) % 7, 'days').startOf('day');

    if (calendarView === 'month') {
      const start = alignToWeekStart(anchorDate.clone().startOf('month'));
      const monthEnd = anchorDate.clone().endOf('month').startOf('day');
      const daysToWeekEnd = (weekStartsOn + 6 - monthEnd.day() + 7) % 7;
      const end = monthEnd.clone().add(daysToWeekEnd + 1, 'days').startOf('day');
      return { from: start.toISOString(), to: end.toISOString() };
    }

    if (calendarView === 'week') {
      const start = alignToWeekStart(anchorDate);
      return { from: start.toISOString(), to: start.clone().add(7, 'days').toISOString() };
    }

    return {
      from: anchorDate.toISOString(),
      to: anchorDate.clone().add(1, 'day').toISOString(),
    };
  }, [selectedDate, calendarView, presenceTimezone, currentLanguage]);

  useEffect(() => {
    if (!canViewShopPresence || !presenceShopId || calendarType === 'employee') {
      setCalendarPresence({ key: '', loading: false, error: false, byDay: {} });
      return;
    }

    const requestKey = `${presenceShopId}:${calendarPresenceRange.from}:${calendarPresenceRange.to}:${presenceTimezone}`;
    if (currentShopPractitioners?.shopId !== presenceShopId) {
      setCalendarPresence({ key: requestKey, loading: true, error: false, byDay: {} });
      return;
    }

    const rangeStart = moment.tz(calendarPresenceRange.from, presenceTimezone);
    const rangeEnd = moment.tz(calendarPresenceRange.to, presenceTimezone);
    const byDay: Record<string, CalendarPresencePerson[]> = {};
    const cursor = rangeStart.clone().startOf('day');
    const end = rangeEnd.clone().startOf('day');

    while (cursor.isBefore(end)) {
      const day = cursor.format('YYYY-MM-DD');
      const result = getShopPresenceForDay(
        currentShopPractitioners.practitioners,
        timeOffs,
        day,
        presenceTimezone,
        presenceShopId,
      );
      byDay[day] = result.people.sort((left, right) =>
        left.firstName.localeCompare(
          right.firstName,
          isFrenchLanguage(currentLanguage) ? 'fr' : 'en',
          { sensitivity: 'base' },
        )
      );
      cursor.add(1, 'day');
    }

    setCalendarPresence({ key: requestKey, loading: false, error: false, byDay });
  }, [
    canViewShopPresence,
    presenceShopId,
    presenceTimezone,
    calendarPresenceRange.from,
    calendarPresenceRange.to,
    calendarType,
    currentLanguage,
    timeOffs,
    currentShopPractitioners,
  ]);

  const visiblePractitionersByDay = useMemo(() => {
    const selectedIds = new Set(effectivePractitionerIds);
    const restrictToSelection = effectivePractitionerIds.length > 0;
    const byDay: Record<string, CalendarPresencePerson[]> = {};

    for (const [day, people] of Object.entries(calendarPresence.byDay)) {
      byDay[day] = people.filter((person) =>
        filteredPractitionerIdSet.has(person.id) &&
        (!restrictToSelection || selectedIds.has(person.id))
      );
    }

    return byDay;
  }, [calendarPresence.byDay, filteredPractitionerIdSet, effectivePractitionerIds]);

  const allowAppointments = calendarType !== 'employee';
  const allowedAppointmentTypes = useMemo(() => {
    switch (calendarType) {
      case 'tattoo':
        return ['TATTOO_SESSION', 'CONSULTATION', 'TOUCH_UP', 'REMOVAL', 'AFTERCARE'] as const;
      case 'piercer':
        return ['PIERCING', 'CONSULTATION', 'AFTERCARE'] as const;
      case 'employee':
        return [] as const;
      default:
        return undefined;
    }
  }, [calendarType]);
  const calendarTypeLabel = useMemo(() => {
    const label = t(`planning.calendar.types.${calendarType}`);
    return label || calendarType;
  }, [calendarType, t]);
  // In week/day views we enforce a strict 15-minute grid so rendered event height
  // is always proportional (e.g. 30 min = half of a 1-hour block).
  const normalizedCalendarStepMinutes = 15;
  const calendarTimeslots = 4;

  // Filter companies to only show those with associated shops
  useEffect(() => {
    if (allCompanies.length > 0) {
      const companiesWithShops = allCompanies.filter((company: any) => 
        shops.some((shop: any) => shop.companyId === company.id)
      );
      setCompanies(companiesWithShops);
    }
  }, [shops, allCompanies]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!practitionerDropdownRef.current) return;
      if (practitionerDropdownRef.current.contains(event.target as Node)) return;
      setShowPractitionerDropdown(false);
    };
    if (showPractitionerDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPractitionerDropdown]);

  const loadCalendarSettings = useCallback(async () => {
    try {
      const response = await apiClient('/api/admin/settings/general');
      if (!response.ok) {
        return;
      }
      const data = await response.json();
      if (data.success && data.data) {
        setCalendarSettings({
          calendarStartHour: data.data.calendarStartHour ?? 7,
          calendarEndHour: data.data.calendarEndHour ?? 22,
          calendarStepMinutes: data.data.calendarStepMinutes ?? 15,
        });
      }
    } catch (error) {
      console.warn('Failed to load calendar settings, using defaults.', error);
    }
  }, []);

  // Keep calendar shop view + data in sync with the header shop selector
  useEffect(() => {
    if (currentView.type !== 'shop') return;
    if (!globalCurrentShopId) return;

    if (globalCurrentShopId === ALL_SHOPS_ID) {
      if (currentView.id !== ALL_SHOPS_ID) {
        setCurrentView({
          type: 'shop',
          id: ALL_SHOPS_ID,
          name: t('planning.calendar.allShops') || 'All shops',
        });
      }
      return;
    }

    const shop =
      contextShops.find((s) => s.id === globalCurrentShopId) ||
      shops.find((s) => s.id === globalCurrentShopId) ||
      (contextCurrentShop?.id === globalCurrentShopId ? contextCurrentShop : null);
    const resolvedShopName = shop?.name || globalCurrentShopId;

    if (
      currentView.id === globalCurrentShopId &&
      currentView.name === resolvedShopName
    ) return;

    setCurrentView({
      type: 'shop',
      id: globalCurrentShopId,
      name: resolvedShopName,
    });
  }, [
    globalCurrentShopId,
    contextShops,
    contextCurrentShop,
    shops,
    currentView.type,
    currentView.id,
    currentView.name,
    t,
  ]);

  // Initial fallback when header has no shop yet and local shops finished loading
  useEffect(() => {
    if (loadingShops || shops.length === 0) return;
    if (currentView.type !== 'shop') return;
    if (globalCurrentShopId || currentView.id) return;
    setCurrentView({ type: 'shop', id: shops[0].id, name: shops[0].name });
  }, [loadingShops, shops, globalCurrentShopId, currentView.type, currentView.id]);

  useEffect(() => {
    loadCalendarSettings();
  }, [loadCalendarSettings]);

  useEffect(() => {
    // Configure moment locale based on current language
    configureMomentLocale(currentLanguage);
    
    // Also set the global moment locale
    if (typeof window !== 'undefined') {
      (window as any).moment = moment;
    }
  }, [currentLanguage]);

  useEffect(() => {
    loadShops();
    loadCompanies();
    loadPractitioners();
    loadActivityTypes();
  }, []);

  useEffect(() => {
    if (calendarType !== 'employee') {
      return;
    }
    const resolvedShopId =
      currentView.type === 'shop' && currentView.id && currentView.id !== ALL_SHOPS_ID && currentView.id !== NO_SHOP_ID
        ? currentView.id
        : undefined;
    loadEmployees(resolvedShopId);
    if (employeeActivityTypes.length === 0) {
      loadEmployeeActivityTypes();
    }
  }, [calendarType, currentView.type, currentView.id]);

  useEffect(() => {
    loadAppointments();
  }, [
    currentView.type,
    currentView.id,
    selectedDate,
    useCustomDateRange,
    fromDate,
    toDate,
    calendarView,
  ]);

  // Load activities with optimized single API call
  const loadActivities = useCallback(async () => {
    try {
      const isAllShops = currentView.type === 'shop' && currentView.id === ALL_SHOPS_ID;
      const isNoShop = currentView.type === 'shop' && currentView.id === NO_SHOP_ID;
      const resolvedShopId = currentView.type === 'shop' ? currentView.id : undefined;
      const resolvedCompanyId =
        currentView.type === 'company' && !currentView.id ? companies[0]?.id : currentView.id;

      if (currentView.type === 'shop' && !isAllShops && !isNoShop && !resolvedShopId) {
        return;
      }
    if (currentView.type === 'company' && !resolvedCompanyId) {
        return;
      }
      // Calculate date range
      let fromDateISO: string;
      let toDateISO: string;
      
      if (useCustomDateRange && fromDate && toDate) {
        fromDateISO = moment(fromDate).startOf('day').toISOString();
        toDateISO = moment(toDate).endOf('day').toISOString();
      } else {
        if (calendarView === 'month') {
          fromDateISO = moment(selectedDate).startOf('month').toISOString();
          toDateISO = moment(selectedDate).endOf('month').toISOString();
        } else if (calendarView === 'week') {
          fromDateISO = getCalendarWeekStart(selectedDate, currentLanguage).toISOString();
          toDateISO = getCalendarWeekEnd(selectedDate, currentLanguage).toISOString();
        } else {
          fromDateISO = moment(selectedDate).startOf('day').toISOString();
          toDateISO = moment(selectedDate).endOf('day').toISOString();
        }
      }

      // Build filter parameters
      const filterParams: any = {
        from: fromDateISO,
        to: toDateISO,
      };

      // Add owner filter if selected
      if (effectivePractitionerIds.length > 0) {
        if (calendarType === 'employee') {
          filterParams.employeeIds = effectivePractitionerIds;
        } else {
          filterParams.practitionerIds = effectivePractitionerIds;
        }
      }

      // Add shop filter based on current view
      if (currentView.type === 'shop' && resolvedShopId && !isAllShops && !isNoShop) {
        // Filter by specific shop
        filterParams.shopIds = [resolvedShopId];
      } else if (currentView.type === 'company' && resolvedCompanyId) {
        // Filter by all shops in the company
        const companyShops = shops.filter(shop => shop.companyId === resolvedCompanyId);
        filterParams.shopIds = companyShops.map(shop => shop.id);
      }
      // If no shop/company selected, load all activities (default behavior)

      // Single API call to get all activities with filters
      const activitiesData = await getAllActivities(filterParams);
      const finalActivities = isNoShop
        ? activitiesData.filter((activity) => !activity.shopId)
        : activitiesData;
      setActivities(finalActivities);
      console.log('📅 Loaded activities:', activitiesData.length, filterParams);

    } catch (error) {
      console.error('Failed to load activities:', error);
      showToast(
        t('planning.calendar.errors.activitiesLoadFailed') || 'Failed to load activities. Showing last loaded data.',
        'warning'
      );
    }
  }, [selectedDate, calendarView, useCustomDateRange, fromDate, toDate, effectivePractitionerIdsKey, currentView, shops, companies, showToast, t]);

  // Load activities when filters change
  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  // Filter appointments and combine with activities when practitioner selection changes
  useEffect(() => {
    let filteredAppointments = allAppointments.filter(isVisibleOnCalendar);
    
    if (effectivePractitionerIds.length > 0) {
      // Filter by selected practitioners
      filteredAppointments = filteredAppointments.filter(apt => 
        effectivePractitionerIds.includes(apt.practitionerId)
      );
    }

    if (calendarType !== 'practitioner' && calendarType !== 'employee') {
      filteredAppointments = filteredAppointments.filter((apt) =>
        filteredPractitionerIdSet.has(apt.practitionerId)
      );
    }

    if (statusFilter) {
      filteredAppointments = filteredAppointments.filter(
        (apt) => (apt.status || '').toUpperCase() === statusFilter.toUpperCase()
      );
    }

    // Format activities as calendar events
    const isAllShopsView = currentView.type === 'shop' && currentView.id === ALL_SHOPS_ID;
    const formattedActivities: Appointment[] = activities
      .filter((activity) => {
        if (calendarType === 'employee') {
          return activity.employeeId ? filteredPractitionerIdSet.has(activity.employeeId) : false;
        }
        if (calendarType === 'practitioner') {
          return true;
        }
        return activity.practitionerId ? filteredPractitionerIdSet.has(activity.practitionerId) : false;
      })
      .map((activity) => {
      const practitionerName = activity.employeeId
        ? (activity.employeeDisplayName ||
            `${activity.employeeFirstName || ''} ${activity.employeeLastName || ''}`.trim() ||
            'Employé inconnu')
        : (activity.practitionerFirstName && activity.practitionerLastName
            ? `${activity.practitionerFirstName} ${activity.practitionerLastName}`
            : 'Praticien Inconnu');
      
      // Ensure dates are properly parsed - startDateTime and endDateTime are always strings
      const startDate = new Date(activity.startDateTime);
      const endDate = new Date(activity.endDateTime);
      
      // Validate dates
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        console.error('Invalid activity dates:', {
          id: activity.id,
          startDateTime: activity.startDateTime,
          endDateTime: activity.endDateTime
        });
      }
      
      const startTime = startDate.toLocaleTimeString(isFrenchLanguage(currentLanguage) ? 'fr-FR' : 'en-GB', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });

      const shopLabel = activity.shopName || '';
      return {
        id: activity.id,
        title: isAllShopsView && shopLabel
          ? `${activity.title} - ${practitionerName} (${startTime}) • ${shopLabel}`
          : `${activity.title} - ${practitionerName} (${startTime})`,
        start: startDate,
        end: endDate,
        allDay: activity.isAllDay || false,
        practitionerId: activity.practitionerId ?? undefined,
        employeeId: activity.employeeId ?? undefined,
        practitionerName: practitionerName,
        clientName: '', // Activities don't have clients
        status: activity.status,
        type: activity.activityTypeName || 'Activity',
        slotCount: 1,
        timezone: activity.timezone,
        shopId: activity.shopId ?? null,
        isActivity: true,
        activityTypeName: activity.activityTypeName,
        effectiveColor: activity.effectiveColor,
        originalData: activity
      };
    });

    const formattedTimeOff: Appointment[] = timeOffs
      .filter((leave) => {
        if (!isApprovedTimeOff(leave.isApproved) && leave.isApproved !== undefined) return false;
        if (calendarType === 'employee') {
          return leave.employeeId ? filteredPractitionerIdSet.has(leave.employeeId) : false;
        }
        if (calendarType === 'practitioner') {
          return Boolean(leave.practitionerId || leave.requesterId);
        }
        return leave.practitionerId ? filteredPractitionerIdSet.has(leave.practitionerId) : false;
      })
      .filter((leave) => {
        if (effectivePractitionerIds.length === 0) return true;
        const ownerId = calendarType === 'employee'
          ? leave.employeeId
          : (leave.practitionerId || leave.requesterId);
        return ownerId ? effectivePractitionerIds.includes(ownerId) : false;
      })
      .map((leave) => {
        const practitionerName = leave.employeeId
          ? (leave.employeeDisplayName ||
              `${leave.employeeFirstName || ''} ${leave.employeeLastName || ''}`.trim() ||
              leave.requesterName ||
              'Employé')
          : (`${leave.practitionerFirstName || ''} ${leave.practitionerLastName || ''}`.trim() ||
              leave.requesterName ||
              'Praticien');
        const startDate = new Date(leave.startDateTime);
        const endDate = new Date(leave.endDateTime);
        const startTime = Number.isNaN(startDate.getTime())
          ? ''
          : startDate.toLocaleTimeString(isFrenchLanguage(currentLanguage) ? 'fr-FR' : 'en-GB', {
              hour: '2-digit',
              minute: '2-digit',
            });
        const timeOffLabel = t('planning.calendar.timeOff') || 'Congé';
        const shopLabel = leave.shopName || '';
        return {
          id: leave.id,
          title: isAllShopsView && shopLabel
            ? `${timeOffLabel} - ${practitionerName}${startTime ? ` (${startTime})` : ''} • ${shopLabel}`
            : `${timeOffLabel} - ${practitionerName}${startTime ? ` (${startTime})` : ''}`,
          start: startDate,
          end: endDate,
          allDay: false,
          practitionerId: leave.practitionerId || leave.requesterId || undefined,
          employeeId: leave.employeeId || undefined,
          practitionerName,
          clientName: '',
          status: 'APPROVED',
          type: timeOffLabel,
          slotCount: 1,
          timezone: leave.timezone || leave.shopTimezone || 'Europe/Paris',
          shopId: leave.shopId ?? null,
          isTimeOff: true,
          originalData: leave,
        };
      });

    // Combine appointments, activities and approved time-off
    const combined = calendarType === 'employee'
      ? [...formattedActivities, ...formattedTimeOff]
      : [...filteredAppointments, ...formattedActivities, ...formattedTimeOff];
    setAppointments(combined);
  }, [effectivePractitionerIds, allAppointments, activities, timeOffs, currentLanguage, calendarType, filteredPractitionerIdSet, statusFilter, currentView.type, currentView.id, t]);

  // Helper function to parse time string (e.g., "10:00") to hours and minutes
  const parseTime = (timeStr: string): { hours: number; minutes: number } => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return { hours, minutes };
  };

  const parseTimeToMinutes = (timeStr?: string): number | null => {
    if (!timeStr || typeof timeStr !== 'string') {
      return null;
    }
    const [hoursStr, minutesStr] = timeStr.split(':');
    const hours = Number(hoursStr);
    const minutes = Number(minutesStr);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return null;
    }
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return null;
    }
    return hours * 60 + minutes;
  };

  const getBoundsFromOpeningHours = (hoursList: unknown[]): { min: Date; max: Date } | null => {
    let minMinutes: number | null = null;
    let maxMinutes: number | null = null;

    for (const openingHours of hoursList) {
      if (!openingHours || typeof openingHours !== 'object') {
        continue;
      }
      for (const dayKey of OPENING_HOUR_DAY_KEYS) {
        const dayHours = (openingHours as OpeningHours)[dayKey];
        const slots = normalizeScheduleSlots(dayHours);
        for (const slot of slots) {
          const openMinutes = parseTimeToMinutes(slot.open);
          const closeMinutes = parseTimeToMinutes(slot.close);
          if (openMinutes === null || closeMinutes === null) {
            continue;
          }
          minMinutes = minMinutes === null ? openMinutes : Math.min(minMinutes, openMinutes);
          maxMinutes = maxMinutes === null ? closeMinutes : Math.max(maxMinutes, closeMinutes);
        }
      }
    }

    if (minMinutes === null || maxMinutes === null || maxMinutes <= minMinutes) {
      return null;
    }

    return {
      min: new Date(2000, 0, 1, Math.floor(minMinutes / 60), minMinutes % 60, 0),
      max: new Date(2000, 0, 1, Math.floor(maxMinutes / 60), maxMinutes % 60, 0),
    };
  };

  // Helper function to calculate date from drop coordinates in month view
  const calculateDateFromCoordinates = (e: React.DragEvent<HTMLDivElement>, calendarElement: HTMLElement, selectedDate: Date): Date => {
    const monthGrid = calendarElement.querySelector('.rbc-month-view');
    if (!monthGrid) {
      return new Date(selectedDate);
    }
    
    const gridRect = monthGrid.getBoundingClientRect();
    const relativeX = e.clientX - gridRect.left;
    const relativeY = e.clientY - gridRect.top;
    
    // Month view has 7 columns (days of week)
    const dayWidth = gridRect.width / 7;
    const weekHeight = gridRect.height / 6; // Approximate 6 weeks
    
    const dayIndex = Math.floor(relativeX / dayWidth);
    const weekIndex = Math.floor(relativeY / weekHeight);
    
    // Get the first day of the month view
    const monthStart = moment(selectedDate).startOf('month');
    const firstDayOfWeek = monthStart.day(); // 0 = Sunday, 1 = Monday, etc.
    const adjustedFirstDay = isFrenchLanguage(currentLanguage) ? (firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1) : firstDayOfWeek;
    
    // Calculate the day
    const dayOffset = weekIndex * 7 + dayIndex - adjustedFirstDay;
    return moment(monthStart).add(dayOffset, 'days').toDate();
  };

  // Calculate calendar time bounds based on opening hours
  const calculateCalendarBounds = () => {
    const fallbackBounds = {
      min: new Date(2000, 0, 1, calendarSettings.calendarStartHour, 0, 0),
      max: new Date(2000, 0, 1, calendarSettings.calendarEndHour, 0, 0),
    };

    if (currentView.type === 'shop' && currentView.id && currentView.id !== ALL_SHOPS_ID && currentView.id !== NO_SHOP_ID) {
      const shop = shops.find((item) => item.id === currentView.id);
      const boundsFromHours = getBoundsFromOpeningHours([shop?.openingHours]);
      return boundsFromHours || fallbackBounds;
    }

    if (currentView.type === 'company' && currentView.id) {
      const companyShops = shops.filter((item) => item.companyId === currentView.id);
      const boundsFromHours = getBoundsFromOpeningHours(companyShops.map((shop) => shop.openingHours));
      return boundsFromHours || fallbackBounds;
    }

    return fallbackBounds;
  };

  // Update calendar bounds when shop/company changes
  useEffect(() => {
    const bounds = calculateCalendarBounds();
    setCalendarMinTime(bounds.min);
    setCalendarMaxTime(bounds.max);
  }, [currentView, shops, calendarSettings]);

  const singleShopForHours =
    currentView.type === 'shop' &&
    currentView.id &&
    currentView.id !== ALL_SHOPS_ID &&
    currentView.id !== NO_SHOP_ID
      ? shops.find((item) => item.id === currentView.id)
      : undefined;

  const openClosedSlotNotice = (start: Date, end: Date) => {
    setClosedSlotNotice({ start, end });
  };

  const isShopSlotClosed = (slotDate: Date) => {
    if (!singleShopForHours) return false;
    const timezone = moment.tz.zone(singleShopForHours.timezone || '')
      ? singleShopForHours.timezone
      : 'Europe/Paris';
    const day = moment(slotDate).format('YYYY-MM-DD');
    const intervals = getShopOpeningIntervals(singleShopForHours.openingHours, day, timezone);
    if (!intervals.length) return true;
    const ms = slotDate.getTime();
    return !intervals.some(([from, to]) => ms >= from && ms < to);
  };

  const isAppointmentInClosedShopHours = (start: Date, end?: Date) => {
    if (!singleShopForHours) return false;
    const timezone = moment.tz.zone(singleShopForHours.timezone || '')
      ? singleShopForHours.timezone
      : 'Europe/Paris';
    const day = moment(start).format('YYYY-MM-DD');
    const intervals = getShopOpeningIntervals(singleShopForHours.openingHours, day, timezone);
    if (!intervals.length) return true;
    const startMs = start.getTime();
    if (!end) {
      return !intervals.some(([from, to]) => startMs >= from && startMs < to);
    }
    const endMs = end.getTime();
    return !intervals.some(([from, to]) => startMs >= from && endMs <= to);
  };

  const loadShops = async () => {
    setLoadingShops(true);
    try {
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      
      // Build query parameters
      const params = new URLSearchParams({
        limit: '100',
        status: 'active'
      });
      
      // Add company filter if user has a company ID
      if (user?.companyId) {
        params.append('companyId', user.companyId);
      }
      
      const response = await apiClient(`/api/admin/shops?${params.toString()}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (data.success) {
        const mappedShops = data.data.shops.map((shop: any) => ({
          id: shop.id,
          name: shop.name,
          companyId: shop.companyId,
          companyName: shop.companyName,
          timezone: shop.timezone || 'Europe/Paris',
          address: shop.address,
          phone: shop.phone,
          email: shop.email,
          openingHours: shop.openingHours || {}
        }));
        mappedShops.sort((a: any, b: any) =>
          String(a.name || '').localeCompare(String(b.name || ''), 'fr', { sensitivity: 'base' })
        );
        setShops(mappedShops);
      }
    } catch (error) {
      console.error('Failed to load shops:', error);
    } finally {
      setLoadingShops(false);
    }
  };

  const loadCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      
      // Build query parameters
      const params = new URLSearchParams({
        limit: '100',
        status: 'active'
      });
      
      // For companies, we only show the user's company unless they're a super admin
      if (user?.companyId && user?.role !== 'super_admin') {
        // Note: This would require a new endpoint to get a specific company by ID
        // For now, we'll filter on the frontend
      }
      
      const response = await apiClient(`/api/admin/companies?${params.toString()}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (data.success) {
        let companies = data.data.companies.map((company: any) => ({
          id: company.id,
          name: company.name,
          legalName: company.legalName,
          email: company.email,
          phone: company.phone
        }));
        
        // Filter companies by user's company ID unless they're a super admin
        if (user?.companyId && user?.role !== 'super_admin') {
          companies = companies.filter((company: any) => company.id === user.companyId);
        }
        
        // Store all companies (will be filtered based on shops in useEffect)
        setAllCompanies(companies);
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const loadPractitioners = async () => {
    setLoadingPractitioners(true);
    try {
      const response = await apiClient('/api/admin/practitioners?limit=100&status=active');
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (data.success) {
        const practitionersList = data.data.practitioners.map((p: any) => ({
          id: p.id,
          personId: p.personId,
          firstName: p.firstName,
          lastName: p.lastName,
          fullName: `${p.firstName} ${p.lastName}`,
          specialties: p.specialties || [],
          color: p.color, // Store practitioner's custom color
          relationshipType: p.relationshipType,
        }));
        
        // Add a synthetic "unassigned" bucket so unassigned appointments can be filtered/colored consistently
        setPractitioners([
          ...practitionersList,
          {
            id: 'UNASSIGNED',
            firstName: '',
            lastName: '',
            fullName: 'Non assigné',
            specialties: [],
            color: '#6B7280',
            relationshipType: 'UNASSIGNED',
          },
        ]);
      }
    } catch (error) {
      console.error('Failed to load practitioners:', error);
    } finally {
      setLoadingPractitioners(false);
    }
  };

  const loadEmployees = async (shopId?: string) => {
    try {
      const params = new URLSearchParams({ limit: '200', status: 'active' });
      if (shopId) {
        params.append('shopId', shopId);
      }
      const response = await apiClient(`/api/admin/employees?${params.toString()}`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      if (data.success) {
        setEmployees(
          (data.data?.employees || []).map((e: any) => ({
            id: e.id,
            personId: e.personId,
            fullName:
              e.person?.displayName ||
              `${e.person?.firstName || ''} ${e.person?.lastName || ''}`.trim() ||
              e.person?.email ||
              'Employé',
            shopIds: Array.isArray(e.employeeShops)
              ? e.employeeShops.map((es: any) => es.shopId)
              : (e.shopId ? [e.shopId] : []),
          }))
        );
      }
    } catch (error) {
      console.error('Failed to load employees:', error);
    }
  };

  // Helper function to calculate date range based on current settings
  const getDateRange = () => {
    let fromDateISO: string;
    let toDateISO: string;
    
    if (useCustomDateRange && fromDate && toDate) {
      // Use custom date range
      fromDateISO = moment(fromDate).startOf('day').toISOString();
      toDateISO = moment(toDate).endOf('day').toISOString();
    } else {
      // Use dynamic range based on calendar view and selected date
      if (calendarView === 'month') {
        // For month view, load the entire month
        fromDateISO = moment(selectedDate).startOf('month').toISOString();
        toDateISO = moment(selectedDate).endOf('month').toISOString();
      } else if (calendarView === 'week') {
        // For week view, load the entire week
        fromDateISO = getCalendarWeekStart(selectedDate, currentLanguage).toISOString();
        toDateISO = getCalendarWeekEnd(selectedDate, currentLanguage).toISOString();
      } else {
        // For day view, load just the day
        fromDateISO = moment(selectedDate).startOf('day').toISOString();
        toDateISO = moment(selectedDate).endOf('day').toISOString();
      }
    }
    
    return { fromDateISO, toDateISO };
  };

  const loadAppointments = async (options?: { silent?: boolean }) => {
    const isAllShops = currentView.type === 'shop' && currentView.id === ALL_SHOPS_ID;
    const isNoShop = currentView.type === 'shop' && currentView.id === NO_SHOP_ID;
    const resolvedShopId = currentView.type === 'shop' ? currentView.id : undefined;
    const resolvedCompanyId =
      currentView.type === 'company' && !currentView.id ? companies[0]?.id : currentView.id;

    if (currentView.type === 'shop' && !isAllShops && !isNoShop && !resolvedShopId) {
      return;
    }
    if (currentView.type === 'company' && !resolvedCompanyId) {
      return;
    }
    if (isNoShop) {
      setAllAppointments([]);
      setTimeOffs([]);
      if (!options?.silent) {
        setLoading(false);
      }
      return;
    }
    if (!options?.silent) {
      setLoading(true);
    }
    try {
      // Determine date range to use
      const { fromDateISO, toDateISO } = getDateRange();
      
      // Determine API endpoint from calendar view only (kept in sync with header shop selector)
      let apiUrl: string;
      if (currentView.type === 'shop' && isAllShops) {
        apiUrl = `/api/planning/calendar/all?from=${fromDateISO}&to=${toDateISO}&allShops=true`;
      } else if ((currentView.type === 'shop' && resolvedShopId) || (currentView.type === 'company' && resolvedCompanyId)) {
        const viewId = currentView.type === 'shop' ? resolvedShopId : resolvedCompanyId;
        apiUrl = `/api/planning/calendar/${currentView.type}/${viewId}?from=${fromDateISO}&to=${toDateISO}&includeActivities=false`;
      } else {
        if (!options?.silent) {
          setLoading(false);
        }
        return;
      }
      
      const response = await apiClient(apiUrl);
      
      if (!response.ok) {
        console.error('Calendar API error:', response.status, response.statusText);
        throw new Error(`API error: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        const isAllShopsView = currentView.type === 'shop' && currentView.id === ALL_SHOPS_ID;
        const formattedAppointments = data.data.appointments.map((apt: any) => {
          
          // Handle both camelCase and lowercase field names from the API
          const clientFirstName = (apt.clientFirstName || apt.clientfirstname || '').trim();
          const clientLastName = (apt.clientLastName || apt.clientlastname || '').trim();
          const practitionerFirstName = apt.practitionerFirstName || apt.practitionerfirstname || '';
          const practitionerLastName = apt.practitionerLastName || apt.practitionerlastname || '';
          const anonymousCode = (
            apt.clientAnonymousCode
            || apt.clientanonymouscode
            || apt.anonymousCode
            || apt.client?.anonymousCode
            || ''
          ).trim();
          const isAnonymousClient =
            apt.clientIsAnonymous === true
            || apt.clientIsAnonymous === 1
            || apt.clientIsAnonymous === '1'
            || apt.clientIsAnonymous === 't'
            || apt.clientIsAnonymous === 'true'
            || apt.client?.isAnonymous === true
            || Boolean(anonymousCode);

          let clientName = clientFirstName && clientLastName
            ? `${clientFirstName} ${clientLastName}`
            : clientFirstName || (isAnonymousClient ? 'Client anonyme' : 'Client Inconnu');
          if (anonymousCode) {
            clientName = `${clientName} (${anonymousCode})`;
          }
          const clientDateOfBirth = apt.clientDateOfBirth ?? apt.client?.dateOfBirth ?? null;
          const rawMinorFlag = apt.clientIsMinor ?? apt.client?.isMinor ?? null;
          const clientIsMinor: boolean | null = clientDateOfBirth
            ? isMinorFromBirthDate(clientDateOfBirth)
            : rawMinorFlag === true
              || rawMinorFlag === 1
              || rawMinorFlag === '1'
              || rawMinorFlag === 'true'
              || rawMinorFlag === 't'
              ? true
              : rawMinorFlag === false
                || rawMinorFlag === 0
                || rawMinorFlag === '0'
                || rawMinorFlag === 'false'
                || rawMinorFlag === 'f'
                ? false
                : null;

          const practitionerName = practitionerFirstName && practitionerLastName
            ? `${practitionerFirstName} ${practitionerLastName}`
            : 'Non assigné';
          const effectivePractitionerId = apt.practitionerId || 'UNASSIGNED';
            
          const appointmentType = apt.type || 'Consultation';
          const startTime = new Date(apt.scheduledAt).toLocaleTimeString(isFrenchLanguage(currentLanguage) ? 'fr-FR' : 'en-GB', { 
            hour: '2-digit', 
            minute: '2-digit' 
          });
          const shopLabel = apt.shopName || apt.shop?.name || '';
          const status = apt.status || 'SCHEDULED';
          
          return {
            id: apt.id,
            title: isAllShopsView && shopLabel
              ? `${clientName} - ${practitionerName} (${startTime}) • ${shopLabel}`
              : `${clientName} - ${practitionerName} (${startTime})`,
            start: new Date(apt.scheduledAt),
            end: new Date(apt.estimatedEndTime),
            allDay: false,
            practitionerId: effectivePractitionerId,
            practitionerName: practitionerName,
            clientName: clientName,
            clientIsMinor,
            status: status,
            type: appointmentType,
            slotCount: apt.slotCount || 1,
            timezone: apt.shopTimezone || 'Europe/Paris',
            shopId: apt.shopId || null,
            // Store original data for modal
            originalData: apt
          };
        });
        
        // Store appointments except cancelled ones — they must not occupy the calendar.
        setAllAppointments(formattedAppointments.filter(isVisibleOnCalendar));
        setTimeOffs(Array.isArray(data.data.timeOff) ? data.data.timeOff : []);
        
        // Activities will be loaded separately in a useEffect that waits for shops to be available
      }
    } catch (error) {
      console.error('Failed to load appointments:', error);
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  };

  const toggleSelectedAppointment = useCallback((appointment: Appointment) => {
    setSelectedAppointment((current) => (current?.id === appointment.id ? null : appointment));
    setShowAppointmentModal(false);
    setContextMenu(null);
    setEventContextMenu(null);
  }, []);

  const handleSelectEvent = (event: Appointment) => {
    console.log('Event selected:', { id: event.id, title: event.title });
    ignoreNextSlotClickRef.current = true;
    toggleSelectedAppointment(event);
    window.setTimeout(() => {
      ignoreNextSlotClickRef.current = false;
    }, 0);
  };

  const handleDoubleClickEvent = (event: Appointment) => {
    ignoreNextSlotClickRef.current = true;
    setSelectedAppointment(event);
    setShowAppointmentModal(true);
    setContextMenu(null);
    setEventContextMenu(null);
    window.setTimeout(() => {
      ignoreNextSlotClickRef.current = false;
    }, 0);
  };

  const resolveContextDate = useCallback(
    (event: React.MouseEvent) => {
      const slotDate = findSlotDateUnderPointer(event, SLOT_TIME_ATTRIBUTE);
      if (slotDate) return slotDate;

      const dayDate = findSlotDateUnderPointer(event, SLOT_DATE_ATTRIBUTE);
      if (dayDate) return normalizeDayCellStart(dayDate);

      const stepMinutes = calendarSettings.calendarStepMinutes || 15;
      return moment(selectedDate)
        .startOf('hour')
        .add(Math.floor(moment(selectedDate).minutes() / stepMinutes) * stepMinutes, 'minutes')
        .toDate();
    },
    [calendarSettings.calendarStepMinutes, selectedDate]
  );

  const handleSlotContextMenu = useCallback(
    (event: React.MouseEvent, startDate: Date) => {
      event.preventDefault();
      event.stopPropagation();
      setEventContextMenu(null);

      const start = startDate instanceof Date ? startDate : new Date(startDate);
      const stepMinutes = calendarSettings.calendarStepMinutes || 15;
      const end = new Date(start.getTime() + stepMinutes * 60 * 1000);

      setContextMenu({
        isOpen: true,
        x: event.clientX,
        y: event.clientY,
        start,
        end,
      });
    },
    [calendarSettings.calendarStepMinutes]
  );

  const handleCalendarContextMenu = useCallback(
    (event: React.MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('.rbc-event')) {
        return;
      }
      const resolvedDate = resolveContextDate(event);
      handleSlotContextMenu(event, resolvedDate);
    },
    [handleSlotContextMenu, resolveContextDate]
  );

  const openContextCreate = useCallback(
    (type: 'activity' | 'appointment', calendarTypeOverride?: 'practitioner' | 'employee') => {
      if (!contextMenu) return;
      if (type === 'appointment' && isAppointmentInClosedShopHours(contextMenu.start, contextMenu.end)) {
        openClosedSlotNotice(contextMenu.start, contextMenu.end);
        setContextMenu(null);
        return;
      }
      setCreateModalData({
        type,
        start: contextMenu.start,
        end: contextMenu.end,
        calendarTypeOverride,
      });
      setShowCreateModal(true);
      setContextMenu(null);
    },
    [contextMenu, singleShopForHours, t, showToast]
  );

  const handleEventContextMenu = useCallback(
    (eventItem: Appointment, event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setContextMenu(null);
      setSelectedAppointment(eventItem);
      setShowAppointmentModal(false);
      setEventContextMenu({
        isOpen: true,
        x: event.clientX,
        y: event.clientY,
        event: eventItem,
      });
    },
    []
  );

  const closeEventContextMenu = useCallback(() => {
    setEventContextMenu(null);
  }, []);

  const openSameSlotAppointment = useCallback((eventItem: Appointment) => {
    if (eventItem.isActivity || eventItem.isTimeOff) return;
    setCreateModalData({
      type: 'appointment',
      start: new Date(eventItem.start),
      end: new Date(eventItem.end),
      shopId: eventItem.shopId ?? eventItem.originalData?.shopId ?? undefined,
      practitionerId:
        eventItem.originalData?.practitionerId
        ?? (eventItem.practitionerId === 'UNASSIGNED' ? null : eventItem.practitionerId)
        ?? null,
    });
    setShowCreateModal(true);
    setEventContextMenu(null);
  }, []);

  useEffect(() => {
    if (!contextMenu?.isOpen) return;

    const handleClose = () => setContextMenu(null);
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
    };

    window.addEventListener('click', handleClose);
    window.addEventListener('scroll', handleClose, true);
    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('click', handleClose);
      window.removeEventListener('scroll', handleClose, true);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [contextMenu?.isOpen]);

  useEffect(() => {
    if (!eventContextMenu?.isOpen) return;

    const handleClose = () => setEventContextMenu(null);
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
    };

    window.addEventListener('click', handleClose);
    window.addEventListener('scroll', handleClose, true);
    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('click', handleClose);
      window.removeEventListener('scroll', handleClose, true);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [eventContextMenu?.isOpen]);

  useEffect(() => {
    if (!showAppointmentModal || servicesLoadedRef.current) return;

    const loadServiceNames = async () => {
      try {
        const response = await apiClient('/api/admin/services?limit=1000');
        if (!response.ok) return;
        const data = await response.json();
        if (!data.success) return;
        const map: Record<string, string> = {};
        (data.data?.services || []).forEach((service: any) => {
          map[service.id] = service.name;
        });
        setServiceNameMap(map);
        servicesLoadedRef.current = true;
      } catch (err) {
        console.error('Failed to load service names:', err);
      }
    };

    void loadServiceNames();
  }, [showAppointmentModal]);

  useEffect(() => {
    const loadActsForAppointment = async () => {
      if (!showAppointmentModal || !selectedAppointment || selectedAppointment.isActivity || selectedAppointment.isTimeOff) {
        setAppointmentActs([]);
        return;
      }

      try {
        setLoadingAppointmentActs(true);
        const result = await getActs({ appointmentId: selectedAppointment.id });
        setAppointmentActs(result.acts);
      } catch (err) {
        console.error('Failed to load appointment acts:', err);
        setAppointmentActs([]);
      } finally {
        setLoadingAppointmentActs(false);
      }
    };

    void loadActsForAppointment();
  }, [selectedAppointment, showAppointmentModal]);

  const loadActivityTypes = async () => {
    try {
      const types = await getActivityTypes();
      setActivityTypes(types);
    } catch (error) {
      console.error('Failed to load activity types:', error);
      setActivityTypes([]);
      showToast(
        error instanceof Error ? error.message : (t('planning.calendar.errors.activityTypesLoadFailed') || 'Impossible de charger les types d\'activité praticien.'),
        'error',
      );
    }
  };

  const loadEmployeeActivityTypes = async () => {
    try {
      const types = await getEmployeeActivityTypes();
      setEmployeeActivityTypes(types);
    } catch (error) {
      console.error('Failed to load employee activity types:', error);
      setEmployeeActivityTypes([]);
      showToast(
        error instanceof Error ? error.message : (t('planning.calendar.errors.employeeActivityTypesLoadFailed') || 'Impossible de charger les types d\'activité employé.'),
        'error',
      );
    }
  };

  const handleSelectSlot = (slotInfo: any) => {
    console.log('=== handleSelectSlot called ===');
    console.log('Slot selected:', slotInfo);
    console.log('Dragged item (state):', draggedItem);
    console.log('Dragged item (ref):', draggedItemRef.current);
    console.log('Show create modal:', showCreateModal);
    console.log('Slot start:', slotInfo.start);
    console.log('Slot end:', slotInfo.end);
    
    // Check both state and ref for dragged item FIRST, before other checks
    const item = draggedItem || draggedItemRef.current;
    
    // If there's a dragged item, open modal immediately
    if (item) {
      console.log('✅ Found dragged item in handleSelectSlot, opening modal for:', item.type);
      // Use the slotInfo dates directly - these are accurate from react-big-calendar
      let { start, end } = slotInfo;
      
      // Ensure start is a Date object
      if (!(start instanceof Date)) {
        start = new Date(start);
      }
      if (!(end instanceof Date)) {
        end = new Date(end);
      }
      
      // For month view, if end is the same as start (or very close), set a default duration
      const duration = end.getTime() - start.getTime();
      const defaultDuration = 60 * 60 * 1000; // 1 hour default
      const calculatedEnd = duration > 1000 * 60 * 5 // More than 5 minutes difference
        ? new Date(end)
        : new Date(start.getTime() + defaultDuration);

      if (item.type === 'appointment' && isAppointmentInClosedShopHours(start, calculatedEnd)) {
        openClosedSlotNotice(start, calculatedEnd);
        setDraggedItem(null);
        draggedItemRef.current = null;
        return;
      }

      console.log('Opening modal for dragged item:', item.type, 'at', start, 'calculated end:', calculatedEnd);
      
      setCreateModalData({
        type: item.type,
        start: new Date(start), // Create new Date instance to avoid reference issues
        end: calculatedEnd,
        activityTypeId: item.activityTypeId,
        employeeActivityTypeId: item.employeeActivityTypeId,
        calendarTypeOverride: item.calendarTypeOverride,
      });
      setShowCreateModal(true);
      setDraggedItem(null);
      draggedItemRef.current = null; // Clear ref too
      return;
    }
    
    // Check if slot selection was triggered by clicking on an event or dragging an event
    // If action is 'select' and there's no dragged item from sidebar, it might be event drag
    if (slotInfo.action === 'select' && !item) {
      const slotStart = slotInfo.start;
      const slotEnd = slotInfo.end || new Date(slotStart.getTime() + 60 * 60 * 1000);
      
      // Check if there's an event in this slot
      const hasEventInSlot = appointments.some(apt => {
        const eventStart = apt.start instanceof Date ? apt.start : new Date(apt.start);
        const eventEnd = apt.end instanceof Date ? apt.end : new Date(apt.end);
        
        // Check if event overlaps with selected slot
        return (eventStart <= slotEnd && eventEnd >= slotStart);
      });
      
      // If there's an event in the slot and we're selecting (not dragging from sidebar),
      // ignore the slot selection to allow event drag to work
      if (hasEventInSlot) {
        console.log('Ignoring slot selection - event exists in slot, allowing event drag');
        return;
      }
    }
    
    // If action is 'click' and there's no dragged item, it's just a regular click
    // We can ignore it or handle it differently
    if (slotInfo.action === 'click' && !item) {
      if (ignoreNextSlotClickRef.current) {
        ignoreNextSlotClickRef.current = false;
        return;
      }
      console.log('Regular click on empty slot (no dragged item)');
      setSelectedAppointment(null);
      setShowAppointmentModal(false);
      setEventContextMenu(null);
      return;
    }
    
    // If no dragged item, just log (could open a generic create modal here)
    console.log('Selected slot (no dragged item):', slotInfo);
  };

  // Handle drag start from sidebar
  const handleDragStart = (item: any) => {
    console.log('handleDragStart called with:', item);
    // Clear any previous dragged item
    setDraggedItem(null);
    draggedItemRef.current = null;
    // Set new dragged item
    setDraggedItem(item);
    draggedItemRef.current = item; // Also store in ref
  };

  // Handle drag end from sidebar - don't clear immediately, wait for drop
  const handleDragEnd = () => {
    console.log('handleDragEnd called');
    // Don't clear the dragged item here - it will be cleared when:
    // 1. Modal opens (in handleSelectSlot or handleDropFromOutside)
    // 2. A new drag starts (in handleDragStart)
    // This ensures the item persists until it's actually used
    // Fallback cleanup: clear after a very long timeout (30 seconds) if modal never opens
    setTimeout(() => {
      // Only clear if modal hasn't opened (check if showCreateModal is still false)
      if (!showCreateModal) {
        console.log('Clearing dragged item after long timeout - no drop detected');
        setDraggedItem(null);
        draggedItemRef.current = null;
      }
    }, 30000); // Very long timeout (30 seconds) to ensure drop is processed
  };

  // Handle drop on calendar from external sidebar
  const handleDropFromOutside = ({ start, end }: { start: Date | string; end: Date | string }) => {
    console.log('=== handleDropFromOutside called ===');
    console.log('Start:', start);
    console.log('End:', end);
    console.log('Calendar view:', calendarView);
    console.log('Selected date:', selectedDate);
    console.log('Dragged item (state):', draggedItem);
    console.log('Dragged item (ref):', draggedItemRef.current);
    
    // Check both state and ref for dragged item
    const item = draggedItem || draggedItemRef.current;
    
    if (!item) {
      console.warn('❌ No dragged item found in state or ref');
      // Try to get item from the drag event if available
      // This is a fallback in case state/ref were cleared
      return;
    }
    
    console.log('✅ Found dragged item in handleDropFromOutside:', item);

    // Ensure dates are Date objects
    let startDate = start instanceof Date ? new Date(start.getTime()) : new Date(start);
    let endDate = end instanceof Date ? new Date(end.getTime()) : new Date(end);
    
    // For month view, react-big-calendar might provide the full day range or current time
    // We need to normalize this to the specific day with a default time
    if (calendarView === 'month') {
      // Extract just the date part (year, month, day)
      const year = startDate.getFullYear();
      const month = startDate.getMonth();
      const day = startDate.getDate();
      
      // If the time is midnight (00:00:00) or the date seems to be just the day start,
      // use a default time of 9:00 AM
      const hours = startDate.getHours();
      const minutes = startDate.getMinutes();
      
      // If it's exactly midnight or very early, assume it's the full day and use default time
      if (hours === 0 && minutes === 0) {
        startDate = new Date(year, month, day, 9, 0, 0);
        endDate = new Date(year, month, day, 10, 0, 0);
      } else {
        // Use the provided time, but ensure end is after start
        startDate = new Date(year, month, day, hours, minutes, 0);
        const duration = endDate.getTime() - startDate.getTime();
        if (duration <= 0) {
          endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hour default
        }
      }
    } else {
      // For week/day view, use the provided times but ensure they're valid
      if (startDate.getTime() >= endDate.getTime()) {
        endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hour default
      }
    }
    
    // Calculate duration - use 1 hour default if end is same as start or very close
    const duration = endDate.getTime() - startDate.getTime();
    const defaultDuration = 60 * 60 * 1000; // 1 hour default
    const calculatedEnd = duration > 1000 * 60 * 5 // More than 5 minutes difference
      ? new Date(endDate)
      : new Date(startDate.getTime() + defaultDuration);

    if (item.type === 'appointment' && isAppointmentInClosedShopHours(startDate, calculatedEnd)) {
      openClosedSlotNotice(startDate, calculatedEnd);
      setDraggedItem(null);
      draggedItemRef.current = null;
      return;
    }

    console.log('Opening modal from drop:', { 
      type: item.type, 
      start: startDate, 
      end: calculatedEnd,
      originalStart: start,
      originalEnd: end,
      duration,
      calendarView
    });

    setCreateModalData({
      type: item.type,
      start: new Date(startDate), // Create new Date instance to avoid reference issues
      end: calculatedEnd,
      activityTypeId: item.activityTypeId,
      employeeActivityTypeId: item.employeeActivityTypeId,
      calendarTypeOverride: item.calendarTypeOverride,
    });
    setShowCreateModal(true);
    setDraggedItem(null);
    draggedItemRef.current = null; // Clear ref too
  };

  // Handle moving existing events
  const handleEventDrop = async ({ event, start, end }: EventInteractionArgs<Appointment>) => {
    console.log('🔄 handleEventDrop called:', { 
      eventId: event.id, 
      eventTitle: event.title,
      oldStart: event.start,
      oldEnd: event.end,
      newStart: start,
      newEnd: end,
      calendarView
    });
    const previousActivities = activities;
    const previousAppointments = allAppointments;

    try {
      setIsMovingEvent(true);
      // Ensure dates are proper Date objects
      let startDate = start instanceof Date ? new Date(start.getTime()) : new Date(start);
      let endDate = end instanceof Date ? new Date(end.getTime()) : new Date(end);
      
      // For month view, if the time is midnight, preserve the original time
      if (calendarView === 'month') {
        const hours = event.start.getHours();
        const minutes = event.start.getMinutes();
        
        // If new start is at midnight, use original time
        if (startDate.getHours() === 0 && startDate.getMinutes() === 0 && 
            (hours !== 0 || minutes !== 0)) {
          startDate.setHours(hours, minutes, 0, 0);
          // Calculate end date preserving duration
          const duration = event.end.getTime() - event.start.getTime();
          endDate = new Date(startDate.getTime() + duration);
        }
      }
      
      // Calculate duration to preserve it if needed
      const originalDuration = event.end.getTime() - event.start.getTime();
      const newDuration = endDate.getTime() - startDate.getTime();
      
      // If the new duration is significantly different (more than 5 minutes), preserve original duration
      if (Math.abs(newDuration - originalDuration) > 5 * 60 * 1000) {
        endDate = new Date(startDate.getTime() + originalDuration);
      }

      if (event.isTimeOff) {
        return;
      }
      if (event.isActivity) {
        // Update activity
        console.log('📋 Updating activity:', event.id, { 
          start: startDate.toISOString(), 
          end: endDate.toISOString() 
        });
        setActivities(prev =>
          prev.map(activity =>
            activity.id === event.id
              ? {
                  ...activity,
                  startDateTime: startDate.toISOString(),
                  endDateTime: endDate.toISOString(),
                }
              : activity
          )
        );
        await updateActivity(
          event.id,
          {
          startDateTime: startDate.toISOString(),
          endDateTime: endDate.toISOString(),
          },
          { skipGlobalLoading: true }
        );
      } else {
        // Update appointment
        console.log('📅 Updating appointment:', event.id, { 
          start: startDate.toISOString(), 
          end: endDate.toISOString() 
        });
        setAllAppointments(prev =>
          prev.map(apt =>
            apt.id === event.id
              ? {
                  ...apt,
                  start: startDate,
                  end: endDate,
                  originalData: {
                    ...apt.originalData,
                    scheduledAt: startDate.toISOString(),
                    estimatedEndTime: endDate.toISOString(),
                  }
                }
              : apt
          )
        );
        const response = await apiClient(`/api/admin/appointments/${event.id}`, {
          method: 'PUT',
          headers: { 'X-Skip-Global-Loading': 'true' },
          body: JSON.stringify({
            scheduledAt: startDate.toISOString(),
            estimatedEndTime: endDate.toISOString(),
            calendarMove: true,
          }),
        });

        const responseData = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(responseData.message || 'Failed to update appointment');
        }
        const notificationPending = responseData.data?.rescheduleNotificationPending === true;
        setAllAppointments(prev =>
          prev.map(apt =>
            apt.id === event.id
              ? {
                  ...apt,
                  originalData: {
                    ...apt.originalData,
                    rescheduleNotificationPending: notificationPending,
                  },
                }
              : apt
          )
        );
      }

      console.log('✅ Event moved successfully');
      showToast(
        t('planning.calendar.moveSuccess') || 'Event moved successfully',
        'success'
      );

      // Refresh in background to ensure consistency (don't block UI)
      void loadAppointments({ silent: true });
      void loadActivities();
    } catch (error) {
      console.error('❌ Failed to move event:', error);
      setActivities(previousActivities);
      setAllAppointments(previousAppointments);
      showToast(
        t('planning.calendar.errors.moveFailed') || 'Failed to move event. Please try again.',
        'error'
      );
    } finally {
      setIsMovingEvent(false);
    }
  };

  useEffect(() => {
    const hideEventDetails = () => {
      if (eventHoverHideTimerRef.current) {
        window.clearTimeout(eventHoverHideTimerRef.current);
        eventHoverHideTimerRef.current = null;
      }
      setEventHover(null);
      setEventHoverCopied(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Shift') {
        isDragModifierPressedRef.current = true;
        setIsDragModifierPressed(true);
        hideEventDetails();
      }
    };
    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Shift') {
        isDragModifierPressedRef.current = false;
        setIsDragModifierPressed(false);
      }
    };
    const handleBlur = () => {
      isDragModifierPressedRef.current = false;
      setIsDragModifierPressed(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('dragstart', hideEventDetails);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('dragstart', hideEventDetails);
    };
  }, []);

  // Handle modal save
  const handleModalSave = async () => {
    await loadAppointments();
    await loadActivities();
  };

  const handleApplyDateFilter = () => {
    if (fromDate && toDate) {
      // Validate that fromDate is before toDate
      if (moment(fromDate).isAfter(moment(toDate))) {
        alert(t('planning.calendar.dateRangeError') || 'From date must be before to date');
        return;
      }
      setUseCustomDateRange(true);
    }
  };

  const handleClearDateFilter = () => {
    setUseCustomDateRange(false);
    setCalendarView(isMobile ? 'day' : 'week');
    if (isMobile) {
      setFromDate(moment().startOf('day').format('YYYY-MM-DD'));
      setToDate(moment().endOf('day').format('YYYY-MM-DD'));
    } else {
      setFromDate(getCalendarWeekStart(moment(), currentLanguage).format('YYYY-MM-DD'));
      setToDate(getCalendarWeekEnd(moment(), currentLanguage).format('YYYY-MM-DD'));
    }
    setSelectedDate(new Date());
  };

  const handleThisWeek = () => {
    setUseCustomDateRange(false);
    setCalendarView('week');
    setFromDate(getCalendarWeekStart(moment(), currentLanguage).format('YYYY-MM-DD'));
    setToDate(getCalendarWeekEnd(moment(), currentLanguage).format('YYYY-MM-DD'));
    setSelectedDate(new Date()); // Reset to current date
  };

  const handleThisMonth = () => {
    setUseCustomDateRange(false);
    setCalendarView('month');
    setFromDate(moment().startOf('month').format('YYYY-MM-DD'));
    setToDate(moment().endOf('month').format('YYYY-MM-DD'));
    setSelectedDate(new Date()); // Reset to current date
  };

  const handleClearAllFilters = () => {
    // Reset all filters to show everything
    const fallbackShop = shops[0];
    const storedShop = globalCurrentShopId ? shops.find(shop => shop.id === globalCurrentShopId) : null;
    const targetShop = storedShop || fallbackShop;
    setCurrentView(targetShop ? { type: 'shop', id: targetShop.id, name: targetShop.name } : { type: 'shop', id: '', name: '' });
    setViewScope(isPractitionerUser ? 'mine' : 'all');
    setSelectedPractitionerIds(isPractitionerUser && ownPractitionerId ? [ownPractitionerId] : []);
    setStatusFilter('');
    setCalendarType('practitioner');
    setUseCustomDateRange(false);
    setCalendarView(isMobile ? 'day' : 'week');
    if (isMobile) {
      setFromDate(moment().startOf('day').format('YYYY-MM-DD'));
      setToDate(moment().endOf('day').format('YYYY-MM-DD'));
    } else {
      setFromDate(getCalendarWeekStart(moment(), currentLanguage).format('YYYY-MM-DD'));
      setToDate(getCalendarWeekEnd(moment(), currentLanguage).format('YYYY-MM-DD'));
    }
    setSelectedDate(new Date());
  };

  // Generate a consistent color for each practitioner based on their ID
  // First checks for stored color, then falls back to hash-based color
  const getPractitionerColor = (practitionerId: string): string => {
    // First check if practitioner has a stored color
    const practitioner = practitioners.find(p => p.id === practitionerId);
    if (practitioner && practitioner.color) {
      return practitioner.color;
    }
    
    // Fallback to hash-based color palette
    const colors = [
      '#3B82F6', // Blue
      '#10B981', // Green
      '#F59E0B', // Amber
      '#EF4444', // Red
      '#8B5CF6', // Purple
      '#EC4899', // Pink
      '#06B6D4', // Cyan
      '#84CC16', // Lime
      '#F97316', // Orange
      '#6366F1', // Indigo
      '#14B8A6', // Teal
      '#A855F7', // Violet
      '#FB7185', // Rose
      '#34D399', // Emerald
      '#FBBF24', // Yellow
    ];
    
    // Create a simple hash from the practitioner ID to get a consistent index
    let hash = 0;
    for (let i = 0; i < practitionerId.length; i++) {
      hash = practitionerId.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const getShopColor = (shopId?: string | null): string => {
    if (!shopId) {
      return '#6B7280';
    }
    const colors = [
      '#2563EB', // Blue
      '#059669', // Green
      '#D97706', // Amber
      '#DC2626', // Red
      '#7C3AED', // Purple
      '#DB2777', // Pink
      '#0891B2', // Cyan
      '#65A30D', // Lime
      '#EA580C', // Orange
      '#4F46E5', // Indigo
    ];
    let hash = 0;
    for (let i = 0; i < shopId.length; i++) {
      hash = shopId.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  // Compact Lucide status icon for calendar cells (never shrinks; readable in week/month)
  const getStatusIcon = (status: string, opts?: { size?: number; withBackdrop?: boolean }) => {
    const key = status?.toUpperCase?.() || '';
    const cfg = STATUS_BADGE_CONFIG[key];
    if (!cfg) return null;
    const Icon = cfg.icon;
    const iconSize = opts?.size ?? 11;
    const withBackdrop = opts?.withBackdrop !== false;
    const box = Math.max(iconSize + 4, 14);

    const iconEl = (
      <Icon
        size={iconSize}
        strokeWidth={2.5}
        color="white"
        aria-hidden
        className="flex-shrink-0"
      />
    );

    if (!withBackdrop) {
      return (
        <span title={cfg.label} style={{ display: 'inline-flex', flexShrink: 0, lineHeight: 0 }}>
          {iconEl}
        </span>
      );
    }

    return (
      <span
        title={cfg.label}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: box,
          height: box,
          borderRadius: 3,
          background: 'rgba(255, 255, 255, 0.28)',
          flexShrink: 0,
          lineHeight: 0,
        }}
      >
        {iconEl}
      </span>
    );
  };

  const truncateText = (value: string, maxLength: number = 200) => {
    if (!value) return '';
    return value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value;
  };

  const getEventStyle = (event: Appointment) => {
    const baseStyle = {
      borderRadius: '4px',
      border: event.isActivity || event.isTimeOff ? '2px dashed rgba(255, 255, 255, 0.8)' : 'none',
      color: 'white',
      padding: calendarView === 'month' ? '1px 4px' : '2px 6px',
      fontSize: calendarView === 'month' ? '11px' : '12px',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      opacity: event.isActivity || event.isTimeOff ? 0.9 : 1,
      cursor: isDragModifierPressed ? 'move' : 'pointer',
      overflow: 'hidden',
    };

    // For activities, use activity color; for appointments, use practitioner color
    const isAllShopsView = currentView.type === 'shop' && currentView.id === ALL_SHOPS_ID;
    const eventShopId = event.shopId ?? event.originalData?.shopId;
    const bookingSource = String(
      event.originalData?.bookingSource || event.originalData?.bookingsource || ''
    ).toUpperCase();
    const backgroundColor = event.isTimeOff
      ? TIME_OFF_EVENT_COLOR
      : (event.isActivity && event.effectiveColor
          ? event.effectiveColor
          : bookingSource === 'BACKOFFICE'
            ? BACKOFFICE_EVENT_COLOR
            : (isAllShopsView && eventShopId
                ? getShopColor(eventShopId)
                : getPractitionerColor(event.practitionerId || event.employeeId || 'UNASSIGNED')));
    
    return { ...baseStyle, backgroundColor };
  };

  // Custom event component — purely presentational.
  // Click / double-click / context-menu are handled by eventWrapper so that
  // events bubble correctly regardless of which child element is clicked.
  const getTypeLabel = (type?: string): { emoji: string; label: string } => {
    switch (type) {
      case 'PIERCING': return { emoji: '💉', label: 'Piercing' };
      case 'TATTOO_SESSION': return { emoji: '🎨', label: 'Tatouage' };
      case 'CONSULTATION': return { emoji: '💬', label: 'Consultation' };
      case 'REMOVAL': return { emoji: '🔴', label: 'Détatouage' };
      case 'AFTERCARE': return { emoji: '🩹', label: 'Suivi' };
      default: return { emoji: '📅', label: type || '' };
    }
  };

  const EventComponent = ({ event }: { event: Appointment }) => {
    const isCompact = calendarView === 'month' || calendarView === 'week';
    const statusIcon = !event.isActivity && !event.isTimeOff
      ? getStatusIcon(event.status, { size: calendarView === 'month' ? 10 : 11 })
      : null;
    const statusTitle = event.isTimeOff
      ? (t('planning.calendar.timeOffApproved') || 'Congé approuvé')
      : event.isActivity
      ? (actStatusLabels[event.status?.toUpperCase?.()] || event.status || '')
      : appointmentStatusLabel(event.status);

    if (event.isTimeOff) {
      return (
        <span
          title={statusTitle ? `${event.title} — ${statusTitle}` : event.title}
          style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0, pointerEvents: 'none' }}
        >
          <span style={{ flexShrink: 0 }}>🏖️</span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{event.title}</span>
        </span>
      );
    }

    if (event.isActivity) {
      return (
        <span
          title={statusTitle ? `${event.title} — ${statusTitle}` : event.title}
          style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0, pointerEvents: 'none' }}
        >
          <span style={{ flexShrink: 0 }}>📋</span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{event.title}</span>
        </span>
      );
    }

    const isUnassignedReserved =
      !!event.originalData?.reservedPractitionerId && !event.originalData?.practitionerId;
    const { emoji: typeEmoji, label: typeLabel } = getTypeLabel(event.type);
    const minorBadge = event.clientIsMinor ? (
      <span
        style={{
          background: '#fef3c7',
          border: '1px solid #fcd34d',
          borderRadius: '999px',
          color: '#92400e',
          flexShrink: 0,
          fontSize: '9px',
          fontWeight: 700,
          lineHeight: '14px',
          padding: '0 4px',
          whiteSpace: 'nowrap',
        }}
      >
        Mineur
      </span>
    ) : null;
    const pendingEmailBadge = hasPendingRescheduleEmail(event) ? (
      <span
        title={t('planning.calendar.contextMenu.rescheduleEmailPending') || 'E-mail de modification à envoyer'}
        style={{
          alignItems: 'center',
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '999px',
          color: '#92400e',
          display: 'inline-flex',
          flexShrink: 0,
          height: '16px',
          justifyContent: 'center',
          width: '16px',
        }}
      >
        <Mail size={10} />
      </span>
    ) : null;

    // Month: icon + title only (type chip does not fit)
    if (calendarView === 'month') {
      return (
        <span
          title={`${statusTitle} · ${typeLabel} · ${event.title}`}
          style={{ display: 'flex', alignItems: 'center', gap: '3px', minWidth: 0, width: '100%', pointerEvents: 'none' }}
        >
          {statusIcon}
          {pendingEmailBadge}
          {minorBadge}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
            {event.title}
          </span>
        </span>
      );
    }

    // Week: small icon + type emoji + title (no padded type chip)
    if (isCompact) {
      return (
        <span
          title={`${statusTitle} · ${typeLabel} · ${event.title}`}
          style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0, width: '100%', pointerEvents: 'none' }}
        >
          {statusIcon}
          {pendingEmailBadge}
          {minorBadge}
          <span style={{ flexShrink: 0, fontSize: '11px', lineHeight: 1 }} title={typeLabel}>
            {typeEmoji}
          </span>
          {isUnassignedReserved && (
            <span
              style={{
                background: 'rgba(255, 255, 255, 0.25)',
                borderRadius: '999px',
                padding: '0px 4px',
                fontSize: '10px',
                lineHeight: '14px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              Réservé
            </span>
          )}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
            {event.title}
          </span>
        </span>
      );
    }

    // Day (and other roomy views): icon + type chip + title
    return (
      <span
        title={`${statusTitle} · ${typeLabel} · ${event.title}`}
        style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0, width: '100%', pointerEvents: 'none' }}
      >
        {statusIcon}
        {pendingEmailBadge}
        {minorBadge}
        <span
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '4px',
            padding: '0px 4px',
            fontSize: '10px',
            fontWeight: 600,
            lineHeight: '16px',
            whiteSpace: 'nowrap',
            letterSpacing: '0.02em',
            flexShrink: 0,
          }}
          title={typeLabel}
        >
          {typeEmoji} {typeLabel}
        </span>
        {isUnassignedReserved && (
          <span
            style={{
              background: 'rgba(255, 255, 255, 0.25)',
              border: '1px solid rgba(255, 255, 255, 0.45)',
              borderRadius: '999px',
              padding: '0px 6px',
              fontSize: '11px',
              lineHeight: '16px',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
            title="Unassigned (reserved capacity)"
          >
            Non assigné (réservé)
          </span>
        )}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
          {event.title}
        </span>
      </span>
    );
  };

  const bookingSourceLabel = (source?: string | null): string => {
    switch (String(source || '').toUpperCase()) {
      case 'ONLINE':
        return 'En ligne';
      case 'BACKOFFICE':
        return 'Backoffice';
      case 'WALK_IN':
        return 'Sans RDV';
      default:
        return '—';
    }
  };

  const buildEventTooltip = (event: Appointment): string => {
    const clientNotesText = clientNotesToPlainText(event.originalData?.clientNotes);
    const startTime = moment(event.start).format('HH:mm');
    const endTime = moment(event.end).format('HH:mm');
    const eventDate = moment(event.start).format('ddd DD/MM/YYYY');
    const statusLabel = event.isTimeOff
      ? (t('planning.calendar.timeOffApproved') || 'Congé approuvé')
      : event.isActivity
      ? (actStatusLabels[event.status?.toUpperCase?.()] || event.status || '—')
      : (appointmentStatusLabel(event.status) || event.status || '—');
    const typeLabel = event.isTimeOff
      ? (t('planning.calendar.timeOff') || 'Congé')
      : event.isActivity
      ? (event.activityTypeName || event.type || 'Activite')
      : (event.type || 'Appointment');
    const shopName =
      event.originalData?.shopName ||
      event.originalData?.shop?.name ||
      shops.find((shop) => shop.id === event.shopId)?.name ||
      undefined;
    const detailsOrNotes = event.originalData?.description || event.originalData?.notes;
    const isAppointment = !event.isActivity && !event.isTimeOff;
    const clientPhone = isAppointment ? getAppointmentClientPhone(event) : '';
    const clientEmail = isAppointment ? getAppointmentClientEmail(event) : '';
    const piercingSummary = isAppointment ? getAppointmentPiercingSummary(event) : '';
    const unknownLabel = t('planning.calendar.hover.unknown') || 'Non renseigné';
    const bookingSource = String(
      event.originalData?.bookingSource || event.originalData?.bookingsource || ''
    );
    const tooltipLines = [
      event.title,
      `${eventDate} - ${startTime} -> ${endTime}`,
      `Statut: ${statusLabel}`,
      ...(isAppointment ? [`Source: ${bookingSourceLabel(bookingSource)}`] : []),
      `Type: ${typeLabel}`,
      ...(piercingSummary
        ? [`${t('planning.calendar.hover.piercing') || 'Piercing'}: ${truncateText(piercingSummary, 160)}`]
        : []),
      ...(shopName ? [`Shop: ${shopName}`] : []),
      ...(event.practitionerName ? [`Praticien: ${event.practitionerName}`] : []),
      ...(isAppointment && event.clientName ? [`Client: ${event.clientName}`] : []),
      ...(isAppointment
        ? [
            `${t('planning.calendar.hover.phone') || 'Téléphone'}: ${clientPhone || unknownLabel}`,
            `${t('planning.calendar.hover.email') || 'E-mail'}: ${clientEmail || unknownLabel}`,
            ...(event.clientIsMinor !== null && event.clientIsMinor !== undefined
              ? [`${t('planning.calendar.hover.minor') || 'Mineur'}: ${
                  event.clientIsMinor
                    ? t('planning.calendar.hover.yes') || 'Oui'
                    : t('planning.calendar.hover.no') || 'Non'
                }`]
              : []),
          ]
        : []),
      ...(event.isTimeOff && event.originalData?.reason ? [`Motif: ${truncateText(String(event.originalData.reason), 240)}`] : []),
      ...(detailsOrNotes ? [`Details: ${truncateText(String(detailsOrNotes), 240)}`] : []),
      ...(clientNotesText ? [`Notes client: ${truncateText(clientNotesText, 240)}`] : []),
    ];
    return tooltipLines.join('\n');
  };

  const clearEventHoverHideTimer = useCallback(() => {
    if (eventHoverHideTimerRef.current) {
      window.clearTimeout(eventHoverHideTimerRef.current);
      eventHoverHideTimerRef.current = null;
    }
  }, []);

  const hideEventHover = useCallback(() => {
    clearEventHoverHideTimer();
    setEventHover(null);
    setEventHoverCopied(false);
  }, [clearEventHoverHideTimer]);

  const scheduleHideEventHover = useCallback(() => {
    clearEventHoverHideTimer();
    eventHoverHideTimerRef.current = window.setTimeout(() => {
      setEventHover(null);
      setEventHoverCopied(false);
      eventHoverHideTimerRef.current = null;
    }, 350);
  }, [clearEventHoverHideTimer]);

  const showEventHover = (
    nativeEvent: React.SyntheticEvent<HTMLElement>,
    event: Appointment
  ) => {
    if (isDragModifierPressedRef.current) return;
    clearEventHoverHideTimer();
    // `eventWrapper` is a static div around the absolutely-positioned `.rbc-event`, so its
    // own rect collapses to the top of the day column and drifts off-screen as the grid
    // scrolls. Anchor on the event box itself to stay next to what is hovered.
    const container = nativeEvent.currentTarget;
    const anchor =
      ((nativeEvent.target as HTMLElement | null)?.closest?.('.rbc-event') as HTMLElement | null)
      || (container.querySelector('.rbc-event') as HTMLElement | null)
      || container;
    const rect = anchor.getBoundingClientRect();
    const tooltipWidth = 320;
    const padding = 8;
    let x = rect.right + 10;
    if (x + tooltipWidth > window.innerWidth - padding) {
      x = Math.max(padding, rect.left - tooltipWidth - 10);
    }
    const anchorTop = rect.top;
    setEventHover({
      text: buildEventTooltip(event),
      x,
      y: Math.max(padding, anchorTop),
      anchorTop,
    });
    setEventHoverCopied(false);
  };

  // The popup is fixed-positioned against the event's viewport rect, but its height is
  // only known once rendered. Clamping against a guessed height lifted the popup away
  // from events sitting low in the scrolled grid, so re-anchor after measuring.
  useLayoutEffect(() => {
    const node = eventHoverRef.current;
    if (!node || !eventHover) return;
    const padding = 8;
    const maxTop = Math.max(padding, window.innerHeight - node.offsetHeight - padding);
    const nextY = Math.min(Math.max(padding, eventHover.anchorTop), maxTop);
    if (Math.abs(nextY - eventHover.y) > 0.5) {
      setEventHover((previous) => (previous ? { ...previous, y: nextY } : previous));
    }
  }, [eventHover]);

  const handleCopyEventHover = useCallback(async () => {
    if (!eventHover?.text) return;
    const copied = await copyTextToClipboard(eventHover.text);
    if (copied) {
      setEventHoverCopied(true);
      window.setTimeout(() => setEventHoverCopied(false), 1800);
      return;
    }
    showToast(t('planning.calendar.hover.copyFailed') || 'Impossible de copier les informations', 'error');
  }, [eventHover, showToast, t]);

  const eventPropGetter = (event: Appointment) => {
    return {
      style: {
        ...getEventStyle(event),
        ...(selectedAppointment?.id === event.id
          ? { boxShadow: '0 0 0 2px #6366F1, 0 0 0 4px rgba(99, 102, 241, 0.35)' }
          : {}),
      },
    };
  };

  const buildPractitionerPresenceTooltip = useCallback((person: CalendarPresencePerson) => {
    const ranges = person.windows.map(([from, to]) =>
      `${moment.tz(from, presenceTimezone).format('HH:mm')}–${moment.tz(to, presenceTimezone).format('HH:mm')}`
    );

    return ranges.length
      ? `${person.fullName}\nHoraires : ${ranges.join(' • ')}`
      : person.fullName;
  }, [presenceTimezone]);

  const showPractitionerPresenceTooltip = useCallback((event: React.MouseEvent<HTMLElement>, person: CalendarPresencePerson) => {
    if (practitionerHoverDismissedIdRef.current === person.id) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipHalfWidth = 130;
    const x = Math.max(tooltipHalfWidth, Math.min(window.innerWidth - tooltipHalfWidth, rect.left + rect.width / 2));

    setPractitionerHover({
      text: buildPractitionerPresenceTooltip(person),
      x,
      y: rect.top - 7,
      personId: person.id,
    });
  }, [buildPractitionerPresenceTooltip]);

  const hidePractitionerPresenceTooltip = useCallback((person: CalendarPresencePerson) => {
    if (practitionerHoverDismissedIdRef.current === person.id) {
      practitionerHoverDismissedIdRef.current = null;
    }
    setPractitionerHover((current) => (current?.personId === person.id ? null : current));
  }, []);

  const dismissPractitionerPresenceTooltip = useCallback((
    event: React.MouseEvent<HTMLElement>,
    person: CalendarPresencePerson
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (practitionerHoverRef.current?.personId !== person.id) return;

    practitionerHoverDismissedIdRef.current = person.id;
    setPractitionerHover(null);
  }, []);

  const calendarOverlayOpen = showAppointmentModal
    || showCreateModal
    || showFiltersPopup
    || showOtherShopAvailability
    || Boolean(contextMenu?.isOpen)
    || Boolean(eventContextMenu?.isOpen)
    || Boolean(appointmentToCancel);

  useEffect(() => {
    if (!calendarOverlayOpen) return;
    practitionerHoverDismissedIdRef.current = null;
    setPractitionerHover(null);
  }, [calendarOverlayOpen]);

  useEffect(() => {
    if (!practitionerHover) return;

    const hideIfLeftName = (event: Event) => {
      if (event.type === 'scroll') {
        setPractitionerHover(null);
        return;
      }
      const target = event.target instanceof Element
        ? event.target
        : event.target instanceof Node
          ? event.target.parentElement
          : null;
      if (target?.closest('[data-practitioner-presence]')) return;
      setPractitionerHover(null);
    };

    window.addEventListener('mousemove', hideIfLeftName);
    window.addEventListener('scroll', hideIfLeftName, true);
    return () => {
      window.removeEventListener('mousemove', hideIfLeftName);
      window.removeEventListener('scroll', hideIfLeftName, true);
    };
  }, [practitionerHover]);

  const presenceHeaderComponents = useMemo(() => {
    const showPresence = canViewShopPresence && !!presenceShopId && calendarType !== 'employee';

    const peopleForDate = (date: Date): CalendarPresencePerson[] => {
      if (!showPresence) return [];
      return visiblePractitionersByDay[moment(date).format('YYYY-MM-DD')] || [];
    };

    const renderPresenceNames = (people: CalendarPresencePerson[], compact: boolean) => (
      <div className={`relative z-[11] flex max-w-full flex-wrap gap-1 overflow-visible pointer-events-auto ${compact ? 'w-full justify-end' : 'justify-center'}`}>
        {people.map((person) => (
          <span
            key={person.id}
            data-practitioner-presence=""
            aria-label={buildPractitionerPresenceTooltip(person)}
            onMouseEnter={(event) => showPractitionerPresenceTooltip(event, person)}
            onMouseLeave={() => hidePractitionerPresenceTooltip(person)}
            onClick={(event) => dismissPractitionerPresenceTooltip(event, person)}
            className={`relative z-[12] max-w-full cursor-default truncate rounded-full bg-emerald-50 px-1.5 py-0.5 font-semibold leading-none text-emerald-700 pointer-events-auto ${compact ? 'text-[9px]' : 'text-[10px]'}`}
          >
            {person.firstName}
          </span>
        ))}
      </div>
    );

    const DayHeader = ({ date, label }: { date: Date; label: React.ReactNode }) => {
      const people = peopleForDate(date);

      return (
        <div
          className="relative z-[10] flex w-full flex-col items-center justify-start gap-1.5 overflow-visible px-1 py-2 pointer-events-auto"
          style={{ minHeight: 112, height: '100%', boxSizing: 'border-box' }}
        >
          <span className="text-xs font-semibold text-gray-800">{label}</span>
          {showPresence && (
            calendarPresence.loading ? (
              <span className="text-[10px] font-medium text-gray-400">…</span>
            ) : people.length > 0 ? renderPresenceNames(people, false) : null
          )}
        </div>
      );
    };

    const MonthDateHeader = ({
      date,
      label,
      onDrillDown,
    }: {
      date: Date;
      label: React.ReactNode;
      onDrillDown?: () => void;
    }) => {
      const people = peopleForDate(date);

      return (
        <div className="relative z-[10] flex w-full flex-col items-end gap-1 overflow-visible pointer-events-auto">
          <button
            type="button"
            onClick={onDrillDown}
            className="text-xs font-semibold text-gray-700 hover:text-gray-950"
          >
            {label}
          </button>
          {showPresence && !calendarPresence.loading && people.length > 0 && renderPresenceNames(people, true)}
        </div>
      );
    };

    return {
      week: { header: DayHeader },
      day: { header: DayHeader },
      month: { dateHeader: MonthDateHeader },
    };
  }, [
    buildPractitionerPresenceTooltip,
    calendarPresence.loading,
    calendarType,
    canViewShopPresence,
    dismissPractitionerPresenceTooltip,
    hidePractitionerPresenceTooltip,
    presenceShopId,
    showPractitionerPresenceTooltip,
    visiblePractitionersByDay,
  ]);

  const calendarComponents = useMemo(
    () => ({
      // Day cells are flex children of `.rbc-row-bg`, so the wrapper has to grow like the
      // cell it replaces, otherwise it collapses and never receives the pointer.
      dateCellWrapper: (props: any) => (
        <div
          className="flex flex-1"
          data-slot-date={props.value instanceof Date ? props.value.toISOString() : undefined}
          onContextMenu={(event) =>
            handleSlotContextMenu(
              event,
              props.value instanceof Date ? normalizeDayCellStart(props.value) : props.value
            )
          }
        >
          {props.children}
        </div>
      ),
      eventWrapper: (props: any) => (
        <div
          aria-label={buildEventTooltip(props.event)}
          onPointerEnter={(event) => showEventHover(event, props.event)}
          onPointerLeave={scheduleHideEventHover}
          onClick={(event) => {
            console.log('Event wrapper left click:', { id: props.event.id, title: props.event.title });
            event.stopPropagation();
            hideEventHover();
            toggleSelectedAppointment(props.event);
          }}
          onDoubleClick={(event) => {
            console.log('Event wrapper double click:', { id: props.event.id, title: props.event.title });
            event.stopPropagation();
            hideEventHover();
            handleDoubleClickEvent(props.event);
          }}
          onContextMenu={(event) => {
            hideEventHover();
            handleEventContextMenu(props.event, event);
          }}
        >
          {props.children}
        </div>
      ),
    }),
    [handleSlotContextMenu, handleEventContextMenu, hideEventHover, scheduleHideEventHover, showEventHover]
  );

  // Tagging the slot itself keeps react-big-calendar's flex layout intact, so the element
  // still has a box that `resolveContextDate` can find under the pointer.
  const slotPropGetter = useCallback(
    (slotDate: Date) => {
      const closed = isShopSlotClosed(slotDate);
      return {
        [SLOT_TIME_ATTRIBUTE]: slotDate instanceof Date ? slotDate.toISOString() : undefined,
        onContextMenu: (event: React.MouseEvent) => handleSlotContextMenu(event, slotDate),
        className: closed ? 'rbc-slot-closed' : undefined,
      } as React.HTMLAttributes<HTMLDivElement>;
    },
    [handleSlotContextMenu, singleShopForHours]
  );

  const handleSendRescheduleEmail = useCallback(
    async (eventItem: Appointment) => {
      if (!hasPendingRescheduleEmail(eventItem) || sendingRescheduleEmailId) return;
      setSendingRescheduleEmailId(eventItem.id);
      try {
        const response = await apiClient(`/api/admin/appointments/${eventItem.id}/send-reschedule-email`, {
          method: 'POST',
          headers: { 'X-Skip-Global-Loading': 'true' },
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.message || 'Failed to send reschedule email');
        }

        setAllAppointments((previous) =>
          previous.map((appointment) =>
            appointment.id === eventItem.id
              ? {
                  ...appointment,
                  originalData: {
                    ...appointment.originalData,
                    rescheduleNotificationPending: false,
                    rescheduleNotificationSentAt: new Date().toISOString(),
                  },
                }
              : appointment
          )
        );
        setEventContextMenu(null);
        showToast(
          t('planning.calendar.contextMenu.rescheduleEmailSent') || 'E-mail de modification envoyé au client.',
          'success'
        );
      } catch (error: any) {
        showToast(
          error.message || t('planning.calendar.contextMenu.rescheduleEmailFailed') || 'Échec de l’envoi de l’e-mail.',
          'error'
        );
      } finally {
        setSendingRescheduleEmailId(null);
      }
    },
    [sendingRescheduleEmailId, showToast, t]
  );

  const handleDuplicateEvent = useCallback(
    async (eventItem: Appointment) => {
      if (eventItem.isTimeOff) return;
      const original = eventItem.originalData || {};
      const start = eventItem.start instanceof Date ? eventItem.start : new Date(eventItem.start);
      const end = eventItem.end instanceof Date ? eventItem.end : new Date(eventItem.end);

      try {
        if (eventItem.isActivity) {
          const activityTypeId = original.activityTypeId;
          if (!activityTypeId) {
            showToast(t('planning.calendar.contextMenu.duplicateMissingActivityType') || 'Activity type is required to duplicate.', 'warning');
            return;
          }
          await createActivity({
            practitionerId: original.employeeId ? undefined : (original.practitionerId ?? eventItem.practitionerId ?? undefined),
            employeeId: original.employeeId ?? eventItem.employeeId ?? undefined,
            shopId: original.shopId ?? eventItem.shopId ?? null,
            activityTypeId,
            employeeActivityTypeId: original.employeeActivityTypeId || undefined,
            title: original.title ?? eventItem.title,
            description: original.description,
            startDateTime: start.toISOString(),
            endDateTime: end.toISOString(),
            isAllDay: original.isAllDay ?? eventItem.allDay ?? false,
          });
          await loadActivities();
          showToast(t('planning.calendar.contextMenu.duplicateSuccess') || 'Duplicated successfully.', 'success');
          return;
        }

        const clientId = original.clientId;
        if (!clientId) {
          showToast(t('planning.calendar.contextMenu.duplicateMissingClient') || 'Client is required to duplicate.', 'warning');
          return;
        }
        const rawPractitionerId = original.practitionerId ?? eventItem.practitionerId ?? null;
        const practitionerId =
          rawPractitionerId && rawPractitionerId !== 'UNASSIGNED' ? rawPractitionerId : null;
        const response = await apiClient('/api/admin/appointments', {
          method: 'POST',
          body: JSON.stringify({
            clientId,
            practitionerId,
            shopId: original.shopId ?? eventItem.shopId ?? null,
            type: original.type ?? eventItem.type,
            scheduledAt: start.toISOString(),
            estimatedEndTime: end.toISOString(),
            description: original.description ?? original.notes,
          }),
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to duplicate appointment');
        }
        await loadAppointments();
        showToast(t('planning.calendar.contextMenu.duplicateSuccess') || 'Duplicated successfully.', 'success');
      } catch (error: any) {
        showToast(error.message || (t('planning.calendar.contextMenu.duplicateFailed') || 'Failed to duplicate.'), 'error');
      } finally {
        setEventContextMenu(null);
      }
    },
    [loadActivities, loadAppointments, showToast, t]
  );

  const openCancelAppointment = useCallback((eventItem: Appointment) => {
    if (!isCancellableAppointment(eventItem)) return;
    setEventContextMenu(null);
    setAppointmentToCancel(eventItem);
    setCancelReason('');
  }, []);

  const closeCancelAppointment = useCallback(() => {
    if (cancellingAppointment) return;
    setAppointmentToCancel(null);
    setCancelReason('');
  }, [cancellingAppointment]);

  const handleConfirmCancelAppointment = useCallback(async () => {
    if (!appointmentToCancel || cancellingAppointment) return;

    const hasClientEmail = Boolean(getAppointmentClientEmail(appointmentToCancel));
    setCancellingAppointment(true);
    try {
      const response = await apiClient(`/api/planning/appointments/${appointmentToCancel.id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({
          reason: cancelReason.trim() || undefined,
          timezone: appointmentToCancel.timezone || 'Europe/Paris',
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t('planning.calendar.modal.cancelAppointmentFailed'));
      }

      const cancelledId = appointmentToCancel.id;
      setAllAppointments((prev) => prev.filter((apt) => apt.id !== cancelledId));
      setAppointments((prev) => prev.filter((apt) => apt.id !== cancelledId));
      setShowAppointmentModal(false);
      setSelectedAppointment(null);
      setAppointmentToCancel(null);
      setCancelReason('');
      showToast(
        hasClientEmail
          ? t('planning.calendar.modal.cancelAppointmentSuccess')
          : t('planning.calendar.modal.cancelAppointmentSuccessNoEmail'),
        hasClientEmail ? 'success' : 'warning'
      );
      void loadAppointments({ silent: true });
    } catch (error: any) {
      showToast(error.message || t('planning.calendar.modal.cancelAppointmentFailed'), 'error');
    } finally {
      setCancellingAppointment(false);
    }
  }, [appointmentToCancel, cancelReason, cancellingAppointment, loadAppointments, showToast, t]);

  const handleDeleteEvent = useCallback(
    async (eventItem: Appointment) => {
      if (eventItem.isTimeOff) return;
      const confirmMessage = eventItem.isActivity
        ? t('planning.calendar.contextMenu.confirmDeleteActivity') || 'Delete this activity?'
        : t('planning.calendar.contextMenu.confirmDeleteAppointment') || 'Delete this appointment?';
      if (!window.confirm(confirmMessage)) return;

      try {
        if (eventItem.isActivity) {
          await deleteActivity(eventItem.id);
          await loadActivities();
        } else {
          const response = await apiClient(`/api/admin/appointments/${eventItem.id}`, {
            method: 'DELETE',
          });
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || 'Failed to delete appointment');
          }
          await loadAppointments();
        }
        showToast(t('planning.calendar.contextMenu.deleteSuccess') || 'Deleted successfully.', 'success');
      } catch (error: any) {
        showToast(error.message || (t('planning.calendar.contextMenu.deleteFailed') || 'Failed to delete.'), 'error');
      } finally {
        setEventContextMenu(null);
      }
    },
    [loadActivities, loadAppointments, showToast, t]
  );

  const mobileActiveFilterCount =
    (selectedPractitionerIds.length > 0 ? 1 : 0) +
    (useCustomDateRange ? 1 : 0) +
    (statusFilter ? 1 : 0);

  const closedSlotDetails = (() => {
    if (!closedSlotNotice) return null;
    const locale = isFrenchLanguage(currentLanguage) ? 'fr-FR' : 'en-GB';
    const timezone = moment.tz.zone(singleShopForHours?.timezone || '')
      ? singleShopForHours?.timezone
      : 'Europe/Paris';
    const dayMoment = moment(closedSlotNotice.start).tz(timezone || 'Europe/Paris');
    const dayKey = availabilityDayNames[dayMoment.day()];
    const periods = normalizeOpeningPeriods(singleShopForHours?.openingHours?.[dayKey]);
    const formatTime = (value: Date) =>
      value.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    return {
      shopName: singleShopForHours?.name || currentView.name || '',
      dateLabel: closedSlotNotice.start.toLocaleDateString(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
      timeLabel: `${formatTime(closedSlotNotice.start)} – ${formatTime(closedSlotNotice.end)}`,
      hoursLabel: periods.length
        ? periods.map((period) => `${period.open} – ${period.close}`).join(', ')
        : t('planning.calendar.shopClosedSlot.closedAllDay'),
    };
  })();

  return (
    <div className="flex flex-col xl:flex-row gap-4">
      <ToastComponent />

      {closedSlotDetails && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <button
            type="button"
            aria-label={t('planning.calendar.shopClosedSlot.close')}
            className="absolute inset-0 bg-gray-900/50"
            onClick={() => setClosedSlotNotice(null)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="shop-closed-slot-title"
            className="relative w-full max-w-lg rounded-xl bg-white shadow-xl"
          >
            <div className="flex items-start gap-3 border-b border-gray-100 px-5 py-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
                <Clock3 className="h-5 w-5 text-amber-700" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 id="shop-closed-slot-title" className="text-lg font-semibold text-gray-900">
                  {t('planning.calendar.shopClosedSlot.title')}
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  {t('planning.calendar.shopClosedSlot.intro')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setClosedSlotNotice(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <dl className="space-y-3 px-5 py-4 text-sm">
              {closedSlotDetails.shopName ? (
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {t('planning.calendar.shopClosedSlot.shop')}
                  </dt>
                  <dd className="mt-0.5 text-gray-900">{closedSlotDetails.shopName}</dd>
                </div>
              ) : null}
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  {t('planning.calendar.shopClosedSlot.requested')}
                </dt>
                <dd className="mt-0.5 capitalize text-gray-900">
                  {closedSlotDetails.dateLabel}
                  <span className="mx-1 text-gray-400">·</span>
                  {closedSlotDetails.timeLabel}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  {t('planning.calendar.shopClosedSlot.hours')}
                </dt>
                <dd className="mt-0.5 text-gray-900">{closedSlotDetails.hoursLabel}</dd>
              </div>
            </dl>
            <p className="mx-5 mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-950">
              {t('planning.calendar.shopClosedSlot.hint')}
            </p>
            <div className="flex justify-end border-t border-gray-100 px-5 py-3">
              <button
                type="button"
                onClick={() => setClosedSlotNotice(null)}
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
              >
                {t('planning.calendar.shopClosedSlot.close')}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      <ConfirmModal
        isOpen={Boolean(appointmentToCancel)}
        title={t('planning.calendar.modal.cancelAppointmentConfirmTitle')}
        message={
          appointmentToCancel
            ? t(
                getAppointmentClientEmail(appointmentToCancel)
                  ? 'planning.calendar.modal.cancelAppointmentConfirmMessage'
                  : 'planning.calendar.modal.cancelAppointmentConfirmMessageNoEmail',
                {
                  client: appointmentToCancel.clientName || t('planning.calendar.modal.client'),
                  when: (() => {
                    const start =
                      appointmentToCancel.start instanceof Date
                        ? appointmentToCancel.start
                        : new Date(appointmentToCancel.start);
                    if (Number.isNaN(start.getTime())) return '-';
                    return start.toLocaleString(isFrenchLanguage(currentLanguage) ? 'fr-FR' : 'en-GB', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    });
                  })(),
                }
              )
            : ''
        }
        confirmLabel={t('planning.calendar.modal.cancelAppointmentConfirm')}
        cancelLabel={t('planning.calendar.modal.cancelAppointmentBack')}
        confirmVariant="danger"
        isLoading={cancellingAppointment}
        onConfirm={() => void handleConfirmCancelAppointment()}
        onClose={closeCancelAppointment}
      >
        <div>
          <label htmlFor="calendar-cancel-reason" className="block text-sm font-medium text-gray-700">
            {t('planning.calendar.modal.cancelAppointmentReason')}
          </label>
          <textarea
            id="calendar-cancel-reason"
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
            disabled={cancellingAppointment}
            rows={3}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 disabled:bg-gray-50"
            placeholder={t('planning.calendar.modal.cancelAppointmentReasonPlaceholder')}
          />
        </div>
      </ConfirmModal>

      {isMobile && (
        <div className="sticky top-0 z-[15] -mx-3 flex w-auto shrink-0 gap-2 bg-gray-50/95 px-3 py-2 backdrop-blur sm:-mx-6 sm:px-6 lg:hidden">
          <button
            type="button"
            onClick={() => {
              setShowFiltersPanel(true);
              setShowFiltersPopup(true);
            }}
            aria-expanded={showFiltersPopup}
            aria-haspopup="dialog"
            className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-gray-800 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {t('planning.calendar.filtersTitle') || 'Filters'}
            {mobileActiveFilterCount > 0 && (
              <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-blue-600 px-1.5 text-[11px] font-semibold text-white">
                {mobileActiveFilterCount}
              </span>
            )}
          </button>
          {canViewShopPresence && (
            <button
              type="button"
              onClick={openOtherShopAvailability}
              aria-haspopup="dialog"
              className="flex shrink-0 items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm font-medium text-emerald-700 shadow-sm hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <Store className="h-4 w-4" aria-hidden="true" />
              Autres shops
            </button>
          )}
        </div>
      )}

      {/* Left Sidebar / mobile filters popup */}
      {((!isMobile && showLeftSidebar) || (isMobile && showFiltersPopup)) && (
        <>
          {isMobile && (
            <div
              className="fixed inset-0 z-[65] bg-gray-950/55 backdrop-blur-[1px]"
              onMouseDown={() => setShowFiltersPopup(false)}
              aria-hidden="true"
            />
          )}
        <aside
          className={
            isMobile
              ? 'fixed inset-x-0 bottom-0 z-[70] flex max-h-[90dvh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl ring-1 ring-black/5'
              : 'w-full xl:w-56 flex-shrink-0 xl:sticky self-start'
          }
          role={isMobile ? 'dialog' : undefined}
          aria-modal={isMobile ? true : undefined}
          aria-labelledby={isMobile ? 'calendar-filters-title' : undefined}
        >
          {isMobile && (
            <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-4 py-3">
              <h2 id="calendar-filters-title" className="text-base font-semibold text-gray-900">
                {t('planning.calendar.filtersTitle') || 'Filters'}
              </h2>
              <button
                type="button"
                onClick={() => setShowFiltersPopup(false)}
                className="inline-flex h-9 items-center rounded-md px-3 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {t('common.close') || 'Close'}
              </button>
            </div>
          )}
          <div className={isMobile ? 'min-h-0 flex-1 overflow-y-auto p-3' : ''}>
            <div className={isMobile ? '' : 'mb-3 bg-white rounded-lg shadow'}>
            {!isMobile && (
            <button
              type="button"
              onClick={() => setShowFiltersPanel((prev) => !prev)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-800 border-b border-gray-100 hover:bg-gray-50"
            >
              <span>{t('planning.calendar.filtersTitle') || 'Filters & Actions'}</span>
              <span className="text-xs text-gray-500">
                {showFiltersPanel ? (t('common.hide') || 'Hide') : (t('common.show') || 'Show')}
              </span>
            </button>
            )}
            {(showFiltersPanel || isMobile) && (
              <div className="p-2 space-y-2">
                {(currentView.id || selectedPractitionerIds.length > 0 || useCustomDateRange || statusFilter) && (
                  <button
                    onClick={handleClearAllFilters}
                    className="w-full flex items-center justify-center gap-2 px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    {t('planning.calendar.clearAllFilters') || 'Clear All Filters'}
                  </button>
                )}

                <div className="space-y-2">
                  {!isManager && (<div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t('planning.calendar.viewType')}
                    </label>
                    <select
                      value={currentView.type}
                      onChange={(e) => {
                        const newType = e.target.value as 'shop' | 'company';
                        if (newType === 'shop') {
                          const storedShop = globalCurrentShopId ? shops.find(shop => shop.id === globalCurrentShopId) : null;
                          const fallbackShop = shops[0];
                          const targetShop = storedShop || fallbackShop;
                          setCurrentView(targetShop ? { type: 'shop', id: targetShop.id, name: targetShop.name } : { type: 'shop', id: '', name: '' });
                        } else {
                          setCurrentView({ type: newType, id: '', name: '' });
                        }
                        setAppointments([]);
                        setAllAppointments([]);
                        setSelectedPractitionerIds([]);
                      }}
                      className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="shop">{t('planning.calendar.shopView')}</option>
                      <option value="company">{t('planning.calendar.companyView')}</option>
                    </select>
                  </div>)}

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t('planning.calendar.calendarType') || 'Calendar type'}
                    </label>
                    <select
                      value={calendarType}
                      onChange={(e) => {
                        const nextType = e.target.value as typeof calendarType;
                        setCalendarType(nextType);
                        const nextPractitioners = getPractitionersForType(nextType);
                        const nextPractitionerIdSet = new Set(nextPractitioners.map((practitioner) => practitioner.id));
                        if (nextType === 'employee') {
                          if (viewScope !== 'all') {
                            setViewScope('all');
                          }
                          if (selectedPractitionerIds.length > 0) {
                            setSelectedPractitionerIds([]);
                          }
                          if (statusFilter) {
                            setStatusFilter('');
                          }
                          return;
                        }
                        if (viewScope === 'mine' && ownPractitionerId && !nextPractitionerIdSet.has(ownPractitionerId)) {
                          setViewScope('all');
                        }
                        if (selectedPractitionerIds.length > 0) {
                          const filtered = selectedPractitionerIds.filter((id) => nextPractitionerIdSet.has(id));
                          if (filtered.length !== selectedPractitionerIds.length) {
                            setSelectedPractitionerIds(filtered);
                          }
                        }
                      }}
                      className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="practitioner">{t('planning.calendar.types.practitioner') || 'Practitioner schedule'}</option>
                      <option value="tattoo">{t('planning.calendar.types.tattoo') || 'Tattoo artist schedule'}</option>
                      <option value="piercer">{t('planning.calendar.types.piercer') || 'Piercer schedule'}</option>
                      <option value="employee">{t('planning.calendar.types.employee') || 'Employee schedule'}</option>
                    </select>
                  </div>

                  {!isManager && (<div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t('planning.calendar.viewScope') || 'Scope'}
                    </label>
                    <select
                      value={viewScope}
                      onChange={(e) => setViewScope(e.target.value as 'all' | 'mine')}
                      disabled={calendarType === 'employee'}
                      className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="all">{t('planning.calendar.viewAll') || 'All shop events'}</option>
                      <option value="mine" disabled={!ownPractitionerId}>
                        {t('planning.calendar.viewMine') || 'My events only'}
                      </option>
                    </select>
                  </div>)}

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {currentView.type === 'shop' ? t('planning.calendar.selectShop') : t('planning.calendar.selectCompany')}
                    </label>
                    <select
                      value={currentView.id}
                      onChange={(e) => {
                        setSelectedPractitionerIds([]);
                        if (currentView.type === 'shop') {
                          if (e.target.value === ALL_SHOPS_ID) {
                            setCurrentView({ ...currentView, id: ALL_SHOPS_ID, name: t('planning.calendar.allShops') || 'All shops' });
                            setCurrentShopId(ALL_SHOPS_ID);
                            return;
                          }
                          if (e.target.value === NO_SHOP_ID) {
                            setCurrentView({ ...currentView, id: NO_SHOP_ID, name: t('planning.calendar.noShop') || 'No shop' });
                            return;
                          }
                          const selected = shops.find(shop => shop.id === e.target.value);
                          if (selected) {
                            setCurrentView({ ...currentView, id: selected.id, name: selected.name });
                            setCurrentShopId(selected.id);
                          }
                        } else {
                          const selected = companies.find(company => company.id === e.target.value);
                          if (selected) {
                            setCurrentView({ ...currentView, id: selected.id, name: selected.name });
                          }
                        }
                      }}
                      disabled={currentView.type === 'shop' ? loadingShops : loadingCompanies}
                      className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="">
                        {currentView.type === 'shop' ? 
                          (loadingShops ? t('planning.calendar.loadingShops') : t('planning.calendar.selectOption')) :
                          (loadingCompanies ? t('planning.calendar.loadingCompanies') : t('planning.calendar.selectOption'))
                        }
                      </option>
                      {currentView.type === 'shop' ? 
                        [
                          ...(currentUser?.role === 'super_admin' ? [
                            <option key={ALL_SHOPS_ID} value={ALL_SHOPS_ID}>
                              {t('planning.calendar.allShops') || 'All shops'}
                            </option>,
                          ] : []),
                          <option key={NO_SHOP_ID} value={NO_SHOP_ID}>
                            {t('planning.calendar.noShop') || 'No shop (activities only)'}
                          </option>,
                          ...shops.map(shop => (
                            <option key={shop.id} value={shop.id}>
                              {shop.name} {shop.companyName ? `(${shop.companyName})` : ''}
                            </option>
                          ))
                        ] :
                        companies.map(company => (
                          <option key={company.id} value={company.id}>
                            {company.name} {company.legalName && company.legalName !== company.name ? `(${company.legalName})` : ''}
                          </option>
                        ))
                      }
                    </select>
                    {currentView.type === 'shop' && (
                      <div className="mt-1 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            if (!currentView.id || currentView.id === ALL_SHOPS_ID || currentView.id === NO_SHOP_ID) return;
                            setCurrentShopId(currentView.id);
                          }}
                          disabled={!currentView.id || currentView.id === ALL_SHOPS_ID || currentView.id === NO_SHOP_ID || currentView.id === globalCurrentShopId}
                          className="px-2.5 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          {currentView.id === globalCurrentShopId
                            ? (t('planning.calendar.currentShopActive') || 'Current shop')
                            : (t('planning.calendar.setCurrentShop') || 'Set as current shop')}
                        </button>
                        {currentView.id && currentView.id === globalCurrentShopId && (
                          <span className="text-xs text-green-700">
                            {t('planning.calendar.currentShopCookieHint') || 'Saved for your next sessions'}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {!isManager && (<div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t('planning.calendar.filterByStatus') || 'Filter by status'}
                    </label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      disabled={calendarType === 'employee'}
                      className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="">{t('planning.calendar.allStatuses') || 'All statuses'}</option>
                      {APPOINTMENT_STATUS_OPTIONS.filter((status) => status !== 'CANCELLED').map((status) => (
                        <option key={status} value={status}>
                          {appointmentStatusLabel(status)}
                        </option>
                      ))}
                    </select>
                  </div>)}

                  <div ref={practitionerDropdownRef}>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t('planning.calendar.filterByPractitioner') || 'Filter by Practitioner(s)'}
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowPractitionerDropdown((prev) => !prev)}
                        disabled={calendarType === 'employee'}
                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-left text-sm bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        {viewScope === 'mine' && ownPractitionerId
                          ? (t('planning.calendar.viewMine') || 'My events only')
                          : (selectedPractitionerIds.length > 0
                              ? `${selectedPractitionerIds.length} ${selectedPractitionerIds.length === 1 ? 'practitioner' : 'practitioners'}`
                              : (t('planning.calendar.allPractitioners') || 'All practitioners'))}
                      </button>
                      {showPractitionerDropdown && (
                        <div className="absolute z-[16] mt-2 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-64 overflow-y-auto">
                          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                            <span className="text-xs text-gray-500">
                              {t('planning.calendar.filterByPractitioner') || 'Filter by Practitioner(s)'}
                            </span>
                            {selectedPractitionerIds.length > 0 && viewScope !== 'mine' && (
                              <button
                                type="button"
                                onClick={() => setSelectedPractitionerIds([])}
                                className="text-xs text-red-600 hover:text-red-800 font-medium"
                              >
                                {t('planning.calendar.clearPractitionerFilter') || 'Clear All'}
                              </button>
                            )}
                          </div>
                          <div className="p-2">
                            {loadingPractitioners ? (
                              <div className="text-sm text-gray-500">Loading practitioners...</div>
                            ) : filteredPractitioners.length === 0 ? (
                              <div className="text-sm text-gray-500">No practitioners available</div>
                            ) : (
                              <div className="space-y-1">
                                {filteredPractitioners.map(practitioner => {
                                  const isSelected = selectedPractitionerIds.includes(practitioner.id);
                                  const practitionerColor = getPractitionerColor(practitioner.id);
                                  return (
                                    <label
                                      key={practitioner.id}
                                      className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 rounded px-2 py-1"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        disabled={viewScope === 'mine' && !!ownPractitionerId}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedPractitionerIds([...selectedPractitionerIds, practitioner.id]);
                                          } else {
                                            setSelectedPractitionerIds(selectedPractitionerIds.filter(id => id !== practitioner.id));
                                          }
                                        }}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                      />
                                      <div className="flex items-center space-x-2 flex-1">
                                        <div
                                          className="w-3 h-3 rounded-full flex-shrink-0 border border-gray-300"
                                          style={{ backgroundColor: practitionerColor }}
                                          title={practitioner.fullName}
                                        />
                                        <span className="text-sm text-gray-700 truncate">{practitioner.fullName}</span>
                                      </div>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {viewScope === 'mine' && ownPractitionerId && (
                      <p className="mt-1 text-xs text-gray-600">
                        {t('planning.calendar.viewMineNote') || 'Showing only your events.'}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-2 pt-1 border-t border-gray-200">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {t('planning.calendar.fromDate')}
                      </label>
                      <DateInput
                        value={fromDate}
                        onChange={(value) => setFromDate(value)}
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {t('planning.calendar.toDate')}
                      </label>
                      <DateInput
                        value={toDate}
                        onChange={(value) => setToDate(value)}
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={handleThisWeek}
                        className="flex-1 px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm whitespace-nowrap"
                        title={t('planning.calendar.thisWeek') || 'This Week'}
                      >
                        📅 {t('planning.calendar.thisWeek') || 'This Week'}
                      </button>

                      <button
                        onClick={handleThisMonth}
                        className="flex-1 px-3 py-1.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm whitespace-nowrap"
                        title={t('planning.calendar.thisMonth') || 'This Month'}
                      >
                        📆 {t('planning.calendar.thisMonth') || 'This Month'}
                      </button>

                      <button
                        onClick={handleApplyDateFilter}
                        disabled={!fromDate || !toDate}
                        className="w-full px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                      >
                        {t('planning.calendar.applyFilter')}
                      </button>

                      {useCustomDateRange && (
                        <button
                          onClick={handleClearDateFilter}
                          className="w-full px-3 py-1.5 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                          title={t('planning.calendar.clearFilter')}
                        >
                          ✕ {t('planning.calendar.clearFilter') || 'Clear'}
                        </button>
                      )}
                    </div>

                    {useCustomDateRange && fromDate && toDate && (
                      <div className="pt-1 border-t border-gray-200">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span className="font-medium">{t('planning.calendar.customRange')}:</span>
                          <span className="ml-2">{moment(fromDate).format('L')} - {moment(toDate).format('L')}</span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            </div>
            {!isMobile && (
              <CalendarDragSidebar
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                allowAppointments={allowAppointments}
              />
            )}
          </div>
        </aside>
        </>
      )}

      {/* Main Content */}
      <div className="flex-1 min-w-0 space-y-2">
        {/* Calendar */}
        <div className="bg-white p-3 rounded-lg shadow">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-base font-semibold text-gray-900 leading-snug">
                    {t('planning.calendar.title')}
                  </h1>
                  <HelpIcon 
                    docPath="08-gestion-planning#08-2-calendrier" 
                    tooltip="Documentation : Calendrier de planning"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 leading-snug">
                  <span>{t('planning.calendar.subtitle')}</span>
                  <span aria-hidden="true">•</span>
                  <span className="font-medium text-gray-700 text-xs">
                    {t('planning.calendar.currentTypeLabel', { type: calendarTypeLabel }) ||
                      `Calendar type: ${calendarTypeLabel}`}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {!isMobile && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      // The filters live inside the left panel: open it first when hidden.
                      if (!showLeftSidebar) {
                        setShowLeftSidebar(true);
                        setShowFiltersPanel(true);
                        return;
                      }
                      setShowFiltersPanel((prev) => !prev);
                    }}
                    aria-pressed={showLeftSidebar && showFiltersPanel}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                      showLeftSidebar && showFiltersPanel
                        ? 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                    }`}
                  >
                    <Filter className="h-4 w-4" aria-hidden="true" />
                    {t('planning.calendar.filtersTitle') || 'Filters'}
                  </button>
                  {canViewShopPresence && (
                    <button
                      type="button"
                      onClick={openOtherShopAvailability}
                      aria-haspopup="dialog"
                      title="Consulter les créneaux libres des praticiens d'un autre shop"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-emerald-300 bg-emerald-50 text-emerald-700 shadow-sm transition-colors hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1"
                    >
                      <Store className="h-4 w-4" aria-hidden="true" />
                      Dispos autres shops
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowLeftSidebar((prev) => !prev)}
                    aria-pressed={showLeftSidebar}
                    title={
                      showLeftSidebar
                        ? t('common.hide') || 'Hide'
                        : t('common.show') || 'Show'
                    }
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                      showLeftSidebar
                        ? 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                    }`}
                  >
                    {showLeftSidebar ? (
                      <PanelLeftClose className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <PanelLeftOpen className="h-4 w-4" aria-hidden="true" />
                    )}
                    {t('planning.calendar.leftPanel') || 'Left panel'}
                  </button>
                </>
              )}
            </div>
          </div>
          <>
            <div className="mt-3 mb-2">
              <h2 className="text-sm font-semibold text-gray-900">
                {currentView.id 
                  ? `${currentView.name} - ${t('planning.calendar.appointments')}`
                  : `${t('planning.calendar.allAppointments') || 'All Appointments'}`
                }
              </h2>
              <p className="text-xs text-gray-600">
                {selectedPractitionerIds.length > 0 || statusFilter
                  ? (calendarType === 'employee'
                      ? `${activities.length} ${t('planning.calendar.activitiesCount', { count: activities.length })} (${t('planning.calendar.filtered') || 'filtered'})`
                      : `${appointments.filter((a) => !a.isActivity && !a.isTimeOff).length} ${t('planning.calendar.appointmentsCount', { count: appointments.filter((a) => !a.isActivity && !a.isTimeOff).length })} · ${activities.length} ${t('planning.calendar.activitiesCount', { count: activities.length })} (${t('planning.calendar.filtered') || 'filtered'})`)
                  : (calendarType === 'employee'
                      ? `${activities.length} ${t('planning.calendar.activitiesCount', { count: activities.length })}`
                      : `${allAppointments.length} ${t('planning.calendar.appointmentsCount', { count: allAppointments.length })} · ${activities.length} ${t('planning.calendar.activitiesCount', { count: activities.length })}`)}
              </p>
              {(selectedPractitionerIds.length > 0 || statusFilter) && (
                <div className="mt-2 flex flex-wrap gap-2">
                    {statusFilter && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
                        {appointmentStatusLabel(statusFilter)}
                        <button
                          type="button"
                          onClick={() => setStatusFilter('')}
                          className="ml-0.5 hover:text-violet-950"
                          aria-label={t('planning.calendar.clearFilter') || 'Clear'}
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {selectedPractitionerIds.map(practitionerId => {
                      const practitioner = filteredPractitioners.find(p => p.id === practitionerId);
                    return practitioner ? (
                      <span key={practitionerId} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {practitioner.fullName}
                      </span>
                    ) : null;
                  })}
                </div>
              )}
            </div>
            
            <style>{`
              /*
               * react-big-calendar keeps the all-day lane immediately after the
               * day-header row. A min-height alone is not enough because that row
               * is a flex item and can still collapse. Give the row an explicit
               * flex-basis/height so wrapped practitioner pills always have their
               * own physical space and can receive hover events.
               */
              .rbc-calendar-wrapper .rbc-time-header-content > .rbc-row.rbc-time-header-cell {
                height: 112px !important;
                min-height: 112px !important;
                flex: 0 0 112px !important;
                overflow: visible !important;
                position: relative !important;
                z-index: 10 !important;
              }
              .rbc-calendar-wrapper .rbc-time-header-content > .rbc-row.rbc-time-header-cell > .rbc-header {
                height: 112px !important;
                min-height: 112px !important;
                overflow: visible !important;
                position: relative !important;
                z-index: 10 !important;
              }
              .rbc-calendar-wrapper .rbc-time-header-content > .rbc-allday-cell {
                margin-top: 0 !important;
                position: relative !important;
                z-index: 1 !important;
              }
              .rbc-calendar-wrapper .rbc-allday-cell,
              .rbc-calendar-wrapper .rbc-allday-cell .rbc-row-content {
                pointer-events: none !important;
              }
              .rbc-calendar-wrapper .rbc-allday-cell .rbc-event,
              .rbc-calendar-wrapper .rbc-allday-cell .rbc-event *,
              .rbc-calendar-wrapper .rbc-allday-cell .rbc-show-more {
                pointer-events: auto !important;
              }
            `}</style>

            <div 
              style={{ height: '600px', position: 'relative' }}
              className="rbc-calendar-wrapper [&_.rbc-time-header-cell]:overflow-visible [&_.rbc-header]:overflow-visible [&_.rbc-allday-cell]:relative [&_.rbc-allday-cell]:z-[1] [&_.rbc-allday-cell]:pointer-events-none [&_.rbc-allday-cell_.rbc-event]:pointer-events-auto [&_.rbc-allday-cell_.rbc-event_*]:pointer-events-auto"
              onContextMenu={handleCalendarContextMenu}
              onDragOver={(e) => {
                const item = draggedItem || draggedItemRef.current;
                if (!item) {
                  return;
                }
                e.preventDefault();
              }}
              aria-busy={loading}
            >
              {isMovingEvent && (
                <div className="absolute top-2 right-2 z-10 pointer-events-none">
                  <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white/90 px-2 py-1 text-xs text-gray-600 shadow-sm">
                    <span className="h-3 w-3 rounded-full border-2 border-gray-300 border-t-primary-500 animate-spin" />
                    Enregistrement...
                  </div>
                </div>
              )}
              <DragAndDropCalendar
                localizer={localizer}
                events={appointments}
                startAccessor="start"
                endAccessor="end"
                style={{ height: '100%' }}
                view={calendarView}
                onView={(view: any) => {
                  if (['month', 'week', 'day'].includes(view)) {
                    setCalendarView(view);
                  }
                }}
                onNavigate={(date: Date) => {
                  setSelectedDate(date);
                  setUseCustomDateRange(false);
                  if (calendarView === 'month') {
                    setFromDate(moment(date).startOf('month').format('YYYY-MM-DD'));
                    setToDate(moment(date).endOf('month').format('YYYY-MM-DD'));
                  } else if (calendarView === 'week') {
                    setFromDate(getCalendarWeekStart(date, currentLanguage).format('YYYY-MM-DD'));
                    setToDate(getCalendarWeekEnd(date, currentLanguage).format('YYYY-MM-DD'));
                  } else {
                    setFromDate(moment(date).startOf('day').format('YYYY-MM-DD'));
                    setToDate(moment(date).endOf('day').format('YYYY-MM-DD'));
                  }
                }}
                date={selectedDate}
                onSelectEvent={handleSelectEvent}
                onDoubleClickEvent={handleDoubleClickEvent}
                onSelectSlot={handleSelectSlot}
                onEventDrop={handleEventDrop}
                dragFromOutsideItem={() => {
                  const item = draggedItem || draggedItemRef.current;
                  if (!item) {
                    return null as any;
                  }
                  const start = new Date();
                  const end = new Date(start.getTime() + 15 * 60 * 1000);
                  return {
                    id: 'external-item',
                    title: item.name,
                    start,
                    end,
                    allDay: false,
                    practitionerId: 'UNASSIGNED',
                    practitionerName: '',
                    clientName: '',
                    status: 'SCHEDULED',
                    type: item.type,
                    slotCount: 1,
                    timezone: 'Europe/Paris',
                  } as Appointment;
                }}
                onDropFromOutside={handleDropFromOutside}
                selectable
                step={normalizedCalendarStepMinutes}
                timeslots={calendarTimeslots}
                dayLayoutAlgorithm={dayLayoutAlgorithm}
                draggableAccessor={(event: Appointment) => !event.isTimeOff && isDragModifierPressedRef.current}
                resizable={false}
                components={{
                  ...calendarComponents,
                  event: EventComponent,
                  ...presenceHeaderComponents,
                }}
                slotPropGetter={slotPropGetter}
                eventPropGetter={eventPropGetter}
                tooltipAccessor={() => ''}
                views={['month', 'week', 'day']}
                culture={currentLanguage}
                messages={messages}
                min={calendarMinTime}
                max={calendarMaxTime}
                popup
              />
              {loading && (
                <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] flex flex-col gap-3 p-4 pointer-events-none">
                  <div className="h-4 w-40 bg-gray-200 rounded animate-pulse"></div>
                  <div className="grid grid-cols-7 gap-2">
                    {Array.from({ length: 7 }).map((_, index) => (
                      <div key={index} className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    ))}
                  </div>
                  <div className="flex-1 grid grid-rows-6 gap-2">
                    {Array.from({ length: 6 }).map((_, index) => (
                      <div key={index} className="h-full bg-gray-200/70 rounded animate-pulse"></div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        </div>

        {/* Practitioner Color Legend */}
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            {t('planning.calendar.practitionerLegend') || 'Practitioner Color Legend'}
          </h3>
          {legendPractitioners.length === 0 ? (
            <p className="text-sm text-gray-500">No practitioners available</p>
          ) : (
            <div className="flex flex-wrap gap-4">
              {legendPractitioners.map(practitioner => {
                const practitionerColor = getPractitionerColor(practitioner.id);
                const isSelected = selectedPractitionerIds.includes(practitioner.id);
                return (
                  <div 
                    key={practitioner.id} 
                    className={`flex items-center space-x-2 px-3 py-1 rounded-md ${isSelected ? 'bg-blue-50 border border-blue-200' : ''}`}
                  >
                    <div 
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: practitionerColor }}
                      title={practitioner.fullName}
                    ></div>
                    <span className={`text-sm ${isSelected ? 'font-semibold text-blue-900' : 'text-gray-700'}`}>
                      {practitioner.fullName}
                    </span>
                    {isSelected && (
                      <span className="text-xs text-blue-600">(✓)</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <p className="mt-3 text-xs text-gray-500 italic">
            {t('planning.calendar.colorLegendNote') || 'Colors in the calendar represent practitioners, not appointment status'}
          </p>
        </div>

        {/* Status Icons Legend */}
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            {t('planning.calendar.statusIconLegend') || 'Appointment Status Icons'}
          </h3>
          <div className="flex flex-wrap gap-4">
            {APPOINTMENT_STATUS_OPTIONS.map((status) => {
              const cfg = STATUS_BADGE_CONFIG[status];
              if (!cfg) return null;
              const Icon = cfg.icon;
              return (
                <div key={status} className="flex items-center space-x-2">
                  <span
                    className={`inline-flex items-center justify-center w-7 h-7 rounded-md ${cfg.bg} ${cfg.text}`}
                  >
                    <Icon size={14} strokeWidth={2.5} aria-hidden />
                  </span>
                  <span className="text-sm text-gray-700">{cfg.label}</span>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-gray-500 italic">
            {t('planning.calendar.statusIconNote') ||
              'A small status icon is shown on each appointment (compact in week/month). Hover for the status label; full badge with icon appears in the details popup.'}
          </p>
        </div>

        {showOtherShopAvailability && (
          <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-gray-950/55 p-4 backdrop-blur-[1px]"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setShowOtherShopAvailability(false);
            }}
          >
            <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5" role="dialog" aria-modal="true" aria-labelledby="other-shop-availability-title">
              <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4 sm:px-6">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700" aria-hidden="true">
                      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="17" rx="2" />
                        <path d="M8 2v4M16 2v4M3 10h18" />
                        <path d="m9 15 2 2 4-4" />
                      </svg>
                    </span>
                    <div className="min-w-0">
                      <h2 id="other-shop-availability-title" className="text-lg font-semibold text-gray-950">Disponibilités des autres shops</h2>
                      <p className="mt-0.5 text-sm text-gray-500">Consultez les plages libres sans changer le shop actif du planning.</p>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowOtherShopAvailability(false)}
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  aria-label="Fermer"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
                </button>
              </div>

              <div className="border-b border-gray-100 bg-gray-50/70 px-5 py-4 sm:px-6">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_220px_auto] sm:items-end">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Shop</label>
                    <select
                      value={availabilityShopId}
                      onChange={(event) => setAvailabilityShopId(event.target.value)}
                      disabled={loadingAvailabilityShops && otherShops.length === 0}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:cursor-wait disabled:bg-gray-100 disabled:text-gray-500"
                    >
                      <option value="">{loadingAvailabilityShops && otherShops.length === 0 ? 'Chargement des shops…' : 'Sélectionner un shop'}</option>
                      {otherShops.map((shop) => (
                        <option key={shop.id} value={shop.id}>{shop.name}{shop.companyName ? ` · ${shop.companyName}` : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Date</label>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => shiftAvailabilityDate(-1)}
                        className="flex h-[42px] w-9 flex-shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        aria-label="Jour précédent"
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
                      </button>
                      <DateInput
                        value={availabilityDate}
                        onChange={(value) => setAvailabilityDate(value)}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                      />
                      <button
                        type="button"
                        onClick={() => shiftAvailabilityDate(1)}
                        className="flex h-[42px] w-9 flex-shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        aria-label="Jour suivant"
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAvailabilityDate(moment().format('YYYY-MM-DD'))}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    Aujourd'hui
                  </button>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6" aria-live="polite">
                {loadingAvailabilityShops && !otherShops.length ? (
                  <div className="flex items-center justify-center py-14 text-sm text-gray-500">Chargement des shops…</div>
                ) : !otherShops.length ? (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-8 text-center">
                    <p className="text-sm font-semibold text-gray-800">Aucun autre shop disponible.</p>
                    <p className="mt-1 text-sm text-gray-500">Votre compte n'a pas accès à un autre shop actif.</p>
                  </div>
                ) : !availabilityShopId ? (
                  <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50/60 px-4 py-10 text-center">
                    <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-white text-gray-400 shadow-sm" aria-hidden="true">↳</span>
                    <p className="mt-3 text-sm font-semibold text-gray-800">Sélectionnez un shop</p>
                    <p className="mt-1 text-sm text-gray-500">Les créneaux disponibles s'afficheront automatiquement pour la date choisie.</p>
                  </div>
                ) : shopAvailability.loading ? (
                  <div className="space-y-3">
                    {[0, 1, 2].map((item) => (
                      <div key={item} className="animate-pulse rounded-xl border border-gray-200 p-4">
                        <div className="h-4 w-40 rounded bg-gray-200" />
                        <div className="mt-3 flex gap-2"><div className="h-8 w-24 rounded-lg bg-gray-100" /><div className="h-8 w-28 rounded-lg bg-gray-100" /></div>
                      </div>
                    ))}
                  </div>
                ) : shopAvailability.error ? (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-4">
                    <p className="text-sm font-semibold text-red-800">Impossible de charger les disponibilités.</p>
                    <p className="mt-1 text-sm text-red-700">{shopAvailability.error}</p>
                  </div>
                ) : shopAvailability.people.length ? (
                  <>
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {allAvailabilityShops.find((shop) => shop.id === availabilityShopId)?.name} · {moment(availabilityDate).format('dddd D MMMM YYYY')}
                        </p>
                        <p className="mt-0.5 text-xs text-gray-500">
                          {shopAvailability.openingIntervals.length
                            ? `Shop ouvert ${shopAvailability.openingIntervals
                                .map(([from, to]) =>
                                  `${moment.tz(from, shopAvailability.timezone).format('HH:mm')}–${moment.tz(to, shopAvailability.timezone).format('HH:mm')}`
                                )
                                .join(', ')} · `
                            : ''}
                          Créneaux libres de 15 minutes minimum, après déduction du planning existant.
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                          {shopAvailability.people.length} {shopAvailability.people.length > 1 ? 'praticiens disponibles' : 'praticien disponible'}
                        </span>
                        {shopAvailability.busy > 0 && (
                          <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">
                            {shopAvailability.busy} complet{shopAvailability.busy > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                      {shopAvailability.people.map((person) => {
                        const initials = person.name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('');
                        const color = getPractitionerColor(person.id);
                        const totalMinutes = person.windows.reduce((sum, [from, to]) => sum + Math.max(0, to - from), 0) / 60000;
                        return (
                          <div key={person.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                            <div className="flex items-center gap-3">
                              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white shadow-sm" style={{ backgroundColor: color }} aria-hidden="true">{initials || 'P'}</span>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-semibold text-gray-950">{person.name}</p>
                                <p className="mt-0.5 text-xs text-gray-500">{formatFreeDuration(totalMinutes)} libres · {person.windows.length} créneau{person.windows.length > 1 ? 'x' : ''}</p>
                              </div>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              {person.windows.map(([from, to], index) => (
                                <span key={`${from}-${to}-${index}`} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2.5 py-1.5 text-sm font-semibold text-emerald-800 ring-1 ring-inset ring-emerald-600/10">
                                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
                                  {moment.tz(from, shopAvailability.timezone).format('HH:mm')}–{moment.tz(to, shopAvailability.timezone).format('HH:mm')}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {shopAvailability.unknown > 0 && (
                      <p className="mt-4 text-xs text-amber-700">Horaires non renseignés pour {shopAvailability.unknown} praticien(s) : ils ne sont pas inclus dans les disponibilités.</p>
                    )}
                  </>
                ) : (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-9 text-center">
                    <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-white text-gray-400 shadow-sm" aria-hidden="true">✓</span>
                    <p className="mt-3 text-sm font-semibold text-gray-800">Aucun créneau disponible</p>
                    <p className="mt-1 text-sm text-gray-500">
                      {allAvailabilityShops.find((shop) => shop.id === availabilityShopId)?.name} · {moment(availabilityDate).format('dddd D MMMM YYYY')}
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      {shopAvailability.busy > 0
                        ? `${shopAvailability.busy} praticien(s) planifié(s) à cette date, mais plus aucun créneau libre.`
                        : 'Aucun praticien n\'est planifié dans ce shop à cette date.'}
                    </p>
                    {shopAvailability.unknown > 0 && (
                      <p className="mt-2 text-xs text-amber-700">Horaires non renseignés pour {shopAvailability.unknown} praticien(s) : ils ne sont pas inclus dans les disponibilités.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {practitionerHover && !calendarOverlayOpen && (
          <div
            className="pointer-events-none fixed z-30 max-w-[260px] -translate-x-1/2 -translate-y-full whitespace-pre-line rounded-lg bg-gray-950 px-3 py-2 text-center text-xs font-medium leading-5 text-white shadow-xl"
            style={{ left: practitionerHover.x, top: practitionerHover.y }}
            role="tooltip"
          >
            {practitionerHover.text}
          </div>
        )}

        {eventHover && !isDragModifierPressed && (
          <div
            ref={eventHoverRef}
            className="fixed z-[90] flex w-[320px] max-w-[calc(100vw-16px)] max-h-[calc(100vh-16px)] flex-col rounded-xl border border-gray-200 bg-white p-3 text-left shadow-xl"
            style={{ left: eventHover.x, top: eventHover.y }}
            role="tooltip"
            onPointerEnter={clearEventHoverHideTimer}
            onPointerLeave={scheduleHideEventHover}
          >
            <div className="mb-2 flex shrink-0 items-start justify-between gap-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                {t('planning.calendar.hover.title') || 'Détails du rendez-vous'}
              </p>
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  void handleCopyEventHover();
                }}
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-100"
              >
                {eventHoverCopied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                {eventHoverCopied
                  ? t('planning.calendar.hover.copied') || 'Copié'
                  : t('planning.calendar.hover.copy') || 'Copier'}
              </button>
            </div>
            <pre className="min-h-0 flex-1 max-h-72 overflow-auto whitespace-pre-wrap break-words font-sans text-xs leading-5 text-gray-800 select-text">
              {eventHover.text}
            </pre>
          </div>
        )}

        {/* Appointment/Activity Details Modal */}
        {showAppointmentModal && selectedAppointment && (
          <div className="fixed inset-0 bg-gray-900/60 overflow-y-auto h-full w-full z-50">
            <div className="relative top-16 mx-auto w-full max-w-2xl rounded-2xl bg-white shadow-xl overflow-hidden">
              {(() => {
                const bookingSource = String(
                  selectedAppointment.originalData?.bookingSource
                  || selectedAppointment.originalData?.bookingsource
                  || ''
                ).toUpperCase();
                const headerColor = selectedAppointment.isTimeOff
                  ? TIME_OFF_EVENT_COLOR
                  : selectedAppointment.isActivity && selectedAppointment.effectiveColor
                  ? selectedAppointment.effectiveColor
                  : bookingSource === 'BACKOFFICE'
                    ? BACKOFFICE_EVENT_COLOR
                    : getPractitionerColor(selectedAppointment.practitionerId || selectedAppointment.employeeId || 'UNASSIGNED');
                const start = selectedAppointment.start instanceof Date ? selectedAppointment.start : new Date(selectedAppointment.start);
                const end = selectedAppointment.end instanceof Date ? selectedAppointment.end : new Date(selectedAppointment.end);
                const timeRange = `${start.toLocaleDateString(isFrenchLanguage(currentLanguage) ? 'fr-FR' : 'en-GB')} ${isFrenchLanguage(currentLanguage) ? 'à' : 'at'} ${start.toLocaleTimeString(isFrenchLanguage(currentLanguage) ? 'fr-FR' : 'en-GB', { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString(isFrenchLanguage(currentLanguage) ? 'fr-FR' : 'en-GB', { hour: '2-digit', minute: '2-digit' })}`;
                const clientNoteSections = getClientNoteSections(selectedAppointment.originalData?.clientNotes);
                return (
                  <>
                    <div className="px-6 py-5 text-white" style={{ backgroundColor: headerColor }}>
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-xs uppercase tracking-wide opacity-80">
                            {selectedAppointment.isTimeOff
                              ? (t('planning.calendar.modal.timeOffTitle') || 'Congé approuvé')
                              : selectedAppointment.isActivity
                              ? (t('planning.calendar.modal.activityTitle') || 'Détails de l\'Activité')
                              : t('planning.calendar.modal.title')}
                          </p>
                          <h3 className="text-xl font-semibold mt-1">{selectedAppointment.title}</h3>
                          <p className="text-sm opacity-90 mt-1">{timeRange}</p>
                          {!selectedAppointment.isActivity && !selectedAppointment.isTimeOff && (
                            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/20 px-2.5 py-1 text-xs font-medium">
                              {getStatusIcon(selectedAppointment.status, { size: 14, withBackdrop: false })}
                              <span>{appointmentStatusLabel(selectedAppointment.status)}</span>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => {
                            setShowAppointmentModal(false);
                            setSelectedAppointment(null);
                          }}
                          className="text-white/80 hover:text-white"
                        >
                          <span className="sr-only">Close</span>
                          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="px-6 py-6 space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {!selectedAppointment.isActivity && !selectedAppointment.isTimeOff && (
                          <div className="rounded-lg border border-gray-200 p-3">
                            <p className="text-xs text-gray-500">{t('planning.calendar.modal.client')}</p>
                            <div className="mt-1 flex items-center gap-2">
                              {selectedAppointment.originalData?.clientId ? (
                                <button
                                  type="button"
                                  onClick={() => navigate(`/clients/${selectedAppointment.originalData.clientId}`)}
                                  className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                  {selectedAppointment.clientName || '-'}
                                </button>
                              ) : (
                                <p className="text-sm font-medium text-gray-900">{selectedAppointment.clientName || '-'}</p>
                              )}
                              {selectedAppointment.clientIsMinor && (
                                <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                                  Mineur
                                </span>
                              )}
                            </div>
                            <p className="mt-2 text-xs text-gray-600">
                              {t('planning.calendar.hover.phone') || 'Téléphone'} : {getAppointmentClientPhone(selectedAppointment) || '—'}
                            </p>
                            <p className="text-xs text-gray-600">
                              {t('planning.calendar.hover.email') || 'E-mail'} : {getAppointmentClientEmail(selectedAppointment) || '—'}
                            </p>
                            {selectedAppointment.originalData?.legalGuardian && (
                              <div className="mt-3 border-t border-amber-200 pt-3 text-xs text-gray-600">
                                <p className="font-semibold uppercase tracking-wide text-amber-700">Tuteur légal</p>
                                <p className="mt-1 font-semibold text-gray-900">
                                  {selectedAppointment.originalData.legalGuardian.firstName}{' '}
                                  {selectedAppointment.originalData.legalGuardian.lastName}
                                </p>
                                {selectedAppointment.originalData.legalGuardian.phone && (
                                  <p className="mt-1">
                                    Téléphone : {selectedAppointment.originalData.legalGuardian.phone}
                                  </p>
                                )}
                                {selectedAppointment.originalData.legalGuardian.email && (
                                  <p>Email : {selectedAppointment.originalData.legalGuardian.email}</p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        <div className="rounded-lg border border-gray-200 p-3">
                          <p className="text-xs text-gray-500">
                            {selectedAppointment.employeeId
                              ? (t('planning.calendar.modal.employee') || 'Employé')
                              : t('planning.calendar.modal.practitioner')}
                          </p>
                          <div className="mt-1 flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900">{selectedAppointment.practitionerName}</p>
                            {!!selectedAppointment.originalData?.reservedPractitionerId &&
                              !selectedAppointment.originalData?.practitionerId && (
                                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 border border-gray-200">
                                  Non assigné (réservé)
                                </span>
                              )}
                          </div>
                        </div>
                        <div className="rounded-lg border border-gray-200 p-3">
                          <p className="text-xs text-gray-500">{t('planning.calendar.modal.type')}</p>
                          <p className="text-sm font-medium text-gray-900 mt-1">{selectedAppointment.type}</p>
                        </div>
                        {!selectedAppointment.isActivity && !selectedAppointment.isTimeOff && (
                          <div className="rounded-lg border border-gray-200 p-3">
                            <p className="text-xs text-gray-500">Source</p>
                            <p className="text-sm font-medium text-gray-900 mt-1">{bookingSourceLabel(bookingSource)}</p>
                          </div>
                        )}
                        <div className="rounded-lg border border-gray-200 p-3">
                          <p className="text-xs text-gray-500">{t('planning.calendar.modal.status')}</p>
                          <div className="mt-1">
                            {selectedAppointment.isTimeOff ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                                {t('planning.calendar.timeOffApproved') || 'Congé approuvé'}
                              </span>
                            ) : selectedAppointment.isActivity ? (
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  actStatusBadgeClasses[selectedAppointment.status?.toUpperCase?.()] ||
                                  'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {actStatusLabels[selectedAppointment.status?.toUpperCase?.()] ||
                                  selectedAppointment.status}
                              </span>
                            ) : (
                              <AppointmentStatusBadge status={selectedAppointment.status} />
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 p-4">
                        <p className="text-xs text-gray-500 mb-2">{t('planning.calendar.modal.duration')}</p>
                        <p className="text-sm font-medium text-gray-900">
                          {(() => {
                            const startDate = selectedAppointment.start instanceof Date ? selectedAppointment.start : new Date(selectedAppointment.start);
                            const endDate = selectedAppointment.end instanceof Date ? selectedAppointment.end : new Date(selectedAppointment.end);
                            if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                              return '-';
                            }
                            const durationMinutes = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));
                            return durationMinutes > 0 ? `${durationMinutes} ${t('planning.calendar.modal.minutes')}` : '-';
                          })()}
                        </p>
                      </div>

                      {selectedAppointment.isTimeOff && selectedAppointment.originalData?.reason && (
                        <div className="rounded-lg border border-gray-200 p-4">
                          <p className="text-xs text-gray-500">{t('planning.calendar.modal.reason') || 'Motif'}</p>
                          <p className="text-sm text-gray-900 mt-1">{selectedAppointment.originalData.reason}</p>
                        </div>
                      )}

                      {selectedAppointment.originalData && (
                        <>
                          {selectedAppointment.originalData.notes && (
                            <div className="rounded-lg border border-gray-200 p-4">
                              <p className="text-xs text-gray-500">{t('planning.calendar.modal.notes')}</p>
                              <p className="text-sm text-gray-900 mt-1">{selectedAppointment.originalData.notes}</p>
                            </div>
                          )}
                          {clientNoteSections.length > 0 && (
                            <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-4">
                              <p className="text-xs text-gray-500 mb-2">Notes client</p>
                              <div className="space-y-3">
                                {clientNoteSections.map((section: ClientNoteSection, index: number) => (
                                  <div key={`${section.title || 'note'}-${index}`} className="bg-blue-50 p-3 rounded-lg">
                                    {section.title && (
                                      <p className="text-xs font-medium text-blue-900 mb-2">{section.title}</p>
                                    )}
                                    <div
                                      className="text-gray-900 text-sm max-w-none
                                        [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2
                                        [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2
                                        [&_li]:my-1 [&_p]:my-1"
                                      dangerouslySetInnerHTML={{ __html: section.html }}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {selectedAppointment.originalData.phone && (
                            <div className="rounded-lg border border-gray-200 p-4">
                              <p className="text-xs text-gray-500">{t('planning.calendar.modal.phone')}</p>
                              <p className="text-sm text-gray-900 mt-1">{selectedAppointment.originalData.phone}</p>
                            </div>
                          )}
                        </>
                      )}

                      {!selectedAppointment.isActivity && !selectedAppointment.isTimeOff && (
                        <div className="rounded-lg border border-gray-200 p-4">
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-xs text-gray-500">Actes associes</p>
                            <span className="text-xs text-gray-400">
                              {appointmentActs.length} acte{appointmentActs.length > 1 ? 's' : ''}
                            </span>
                          </div>
                          {loadingAppointmentActs ? (
                            <p className="text-sm text-gray-500">Chargement des actes...</p>
                          ) : appointmentActs.length === 0 ? (
                            <p className="text-sm text-gray-500">Aucun acte associe</p>
                          ) : (
                            <div className="space-y-2">
                              {appointmentActs.slice(0, 3).map((act) => (
                                <div
                                  key={act.id}
                                  className="flex items-center justify-between gap-3 rounded-md bg-gray-50 px-3 py-2"
                                >
                                  <div>
                                    <p className="text-xs text-gray-500">Service</p>
                                    <p className="text-sm font-medium text-gray-900">
                                      {serviceNameMap[act.serviceId] || act.serviceId}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {act.performedAt
                                        ? new Date(act.performedAt).toLocaleString(isFrenchLanguage(currentLanguage) ? 'fr-FR' : 'en-GB', {
                                            dateStyle: 'short',
                                            timeStyle: 'short',
                                          })
                                        : '-'}
                                    </p>
                                  </div>
                                  <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      actStatusBadgeClasses[act.status] || 'bg-gray-100 text-gray-700'
                                    }`}
                                  >
                                    {actStatusLabels[act.status] || act.status}
                                  </span>
                                </div>
                              ))}
                              {appointmentActs.length > 3 && (
                                <p className="text-xs text-gray-500">+{appointmentActs.length - 3} autres actes</p>
                              )}
                            </div>
                          )}
                          <div className="pt-2">
                            <button
                              onClick={() => {
                                const original = selectedAppointment.originalData || {};
                                const params = new URLSearchParams();
                                if (selectedAppointment.id) params.set('appointmentId', selectedAppointment.id);
                                if (original.shopId) params.set('shopId', original.shopId);
                                if (original.practitionerId) params.set('practitionerId', original.practitionerId);
                                if (original.clientId) params.set('clientId', original.clientId);
                                if (original.serviceId) params.set('serviceId', original.serviceId);
                                navigate(`/planning/acts/create?${params.toString()}`);
                              }}
                              className="text-sm text-indigo-600 hover:text-indigo-700"
                            >
                              Creer un acte associe
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="flex flex-wrap justify-end gap-3 pt-2">
                        {canCancelAppointments && isCancellableAppointment(selectedAppointment) && (
                          <button
                            type="button"
                            onClick={() => openCancelAppointment(selectedAppointment)}
                            className="mr-auto px-4 py-2 border border-red-200 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                          >
                            {t('planning.calendar.modal.cancelAppointment')}
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setShowAppointmentModal(false);
                            setSelectedAppointment(null);
                          }}
                          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          {t('planning.calendar.modal.close')}
                        </button>
                        {!selectedAppointment.isTimeOff && (
                          <>
                            <button
                              onClick={() => {
                                const original = selectedAppointment.originalData || {};
                                const practitionerId = selectedAppointment.practitionerId === 'UNASSIGNED'
                                  ? null
                                  : selectedAppointment.practitionerId;
                                setShowAppointmentModal(false);
                                setSelectedAppointment(null);
                                setCreateModalData({
                                  type: selectedAppointment.isActivity ? 'activity' : 'appointment',
                                  start: selectedAppointment.start,
                                  end: selectedAppointment.end,
                                  activityId: selectedAppointment.isActivity ? selectedAppointment.id : undefined,
                                  appointmentId: selectedAppointment.isActivity ? undefined : selectedAppointment.id,
                                  activityTypeId: selectedAppointment.isActivity ? original.activityTypeId : undefined,
                                  employeeActivityTypeId: selectedAppointment.isActivity ? original.employeeActivityTypeId : undefined,
                                  appointmentType: selectedAppointment.isActivity ? undefined : original.type,
                                  shopId: original.shopId,
                                  practitionerId: practitionerId,
                                  employeeId: selectedAppointment.isActivity ? (selectedAppointment.employeeId || null) : undefined,
                                  clientId: selectedAppointment.isActivity ? undefined : original.clientId,
                                  title: selectedAppointment.isActivity ? original.title : undefined,
                                  description: selectedAppointment.isActivity ? original.description : original.notes,
                                  isAllDay: selectedAppointment.isActivity ? original.isAllDay : undefined,
                                });
                                setShowCreateModal(true);
                              }}
                              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                            >
                              {t('planning.calendar.modal.quickEdit') || 'Quick Edit'}
                            </button>
                            <button
                              onClick={() => {
                                if (selectedAppointment.isActivity) {
                                  navigate(`/planning/activities/${selectedAppointment.id}/edit`);
                                } else {
                                  navigate(`/appointments/${selectedAppointment.id}/edit`);
                                }
                              }}
                              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                            >
                              {t('planning.calendar.modal.edit')}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Create/Edit Modal */}
        {showCreateModal && createModalData && (
          createModalData.type === 'appointment' && !createModalData.appointmentId ? (
            <div className="fixed inset-0 z-[60] bg-gray-900/60 p-4 sm:p-6" role="dialog" aria-modal="true">
              <div className="relative mx-auto h-full max-w-6xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateModalData(null);
                  }}
                  className="absolute right-4 top-4 z-20 rounded-full border border-gray-200 bg-white p-2 text-gray-500 shadow hover:bg-gray-50 hover:text-gray-800"
                  aria-label={t('common.close') || 'Fermer'}
                >
                  <X className="h-5 w-5" />
                </button>
                <AppointmentCreatePage
                  embedded
                  initialData={{
                    shopId:
                      createModalData.shopId
                      || (
                        currentView.type === 'shop'
                        && currentView.id !== ALL_SHOPS_ID
                        && currentView.id !== NO_SHOP_ID
                          ? currentView.id
                          : undefined
                      ),
                    practitionerId: createModalData.practitionerId,
                    scheduledAt: moment(createModalData.start).format('YYYY-MM-DDTHH:mm'),
                    estimatedEndTime: moment(createModalData.end).format('YYYY-MM-DDTHH:mm'),
                  }}
                  onClose={() => {
                    setShowCreateModal(false);
                    setCreateModalData(null);
                  }}
                  onCreated={async () => {
                    setShowCreateModal(false);
                    setCreateModalData(null);
                    await loadAppointments();
                    showToast(
                      t('planning.calendar.contextMenu.createAppointmentSuccess') || 'Rendez-vous créé avec succès.',
                      'success'
                    );
                  }}
                />
              </div>
            </div>
          ) : (
            <CalendarItemModal
              isOpen={showCreateModal}
              onClose={() => {
                setShowCreateModal(false);
                setCreateModalData(null);
              }}
              onSave={handleModalSave}
              onToast={showToast}
              type={createModalData.type}
              calendarType={createModalData.calendarTypeOverride ?? calendarType}
              initialData={createModalData}
              shops={shops}
              practitioners={
                createModalData.calendarTypeOverride
                  ? getPractitionersForType(createModalData.calendarTypeOverride)
                  : filteredPractitioners
              }
              activityTypes={activityTypes}
              employeeActivityTypes={employeeActivityTypes}
              allowedAppointmentTypes={allowedAppointmentTypes}
              selectedShopId={
                createModalData.appointmentId || createModalData.activityId
                  ? undefined
                  : (currentView.type === 'shop' && currentView.id && currentView.id !== ALL_SHOPS_ID && currentView.id !== NO_SHOP_ID
                      ? currentView.id
                      : undefined)
              }
            />
          )
        )}

        {contextMenu?.isOpen && (
          <div className="fixed inset-0 z-50 pointer-events-none">
            <div
              className="absolute bg-white border border-gray-200 rounded-xl shadow-xl py-2 w-64 pointer-events-auto"
              style={{ top: contextMenu.y, left: contextMenu.x }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="px-3 pb-2 text-[11px] uppercase tracking-wide text-gray-400">
                {t('planning.calendar.contextMenu.createTitle') || 'Créer'}
              </div>
              <div className="flex flex-col gap-1 px-2">
                <button
                  type="button"
                  onClick={() => openContextCreate('appointment')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  {t('planning.calendar.contextMenu.createAppointment') || 'Créer un rendez-vous'}
                </button>
                <button
                  type="button"
                  onClick={() => openContextCreate('activity', 'practitioner')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  {t('planning.calendar.contextMenu.createActivity') || 'Créer une activité'}
                </button>
                <button
                  type="button"
                  onClick={() => openContextCreate('activity', 'employee')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  {t('planning.calendar.contextMenu.createEmployeeActivity') || 'Créer une activité employé'}
                </button>
              </div>
            </div>
          </div>
        )}

        {eventContextMenu?.isOpen && (
          <div className="fixed inset-0 z-50 pointer-events-none">
            <div
              className="absolute bg-white border border-gray-200 rounded-xl shadow-xl py-2 w-64 pointer-events-auto"
              style={{ top: eventContextMenu.y, left: eventContextMenu.x }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="px-3 pb-2 text-[11px] uppercase tracking-wide text-gray-400">
                {t('planning.calendar.contextMenu.actionsTitle') || 'Actions'}
              </div>
              <div className="flex flex-col gap-1 px-2">
                <button
                  type="button"
                  onClick={() => handleDoubleClickEvent(eventContextMenu.event)}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  {eventContextMenu.event.isTimeOff
                    ? (t('planning.calendar.contextMenu.view') || 'Voir')
                    : (t('planning.calendar.contextMenu.edit') || 'Edit')}
                </button>
                {!eventContextMenu.event.isTimeOff && (
                  <>
                    {!eventContextMenu.event.isActivity && (
                      <button
                        type="button"
                        onClick={() => openSameSlotAppointment(eventContextMenu.event)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-50"
                      >
                        <Plus className="w-4 h-4" />
                        {t('planning.calendar.contextMenu.createAppointmentSameSlot') || 'Créer un rendez-vous sur ce créneau'}
                      </button>
                    )}
                    {!eventContextMenu.event.isActivity && hasPendingRescheduleEmail(eventContextMenu.event) && (
                      <button
                        type="button"
                        onClick={() => handleSendRescheduleEmail(eventContextMenu.event)}
                        disabled={sendingRescheduleEmailId === eventContextMenu.event.id}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-800 rounded-lg bg-amber-50 hover:bg-amber-100 disabled:opacity-60"
                      >
                        <Mail className="w-4 h-4" />
                        {sendingRescheduleEmailId === eventContextMenu.event.id
                          ? (t('planning.calendar.contextMenu.rescheduleEmailSending') || 'Envoi en cours…')
                          : (t('planning.calendar.contextMenu.sendRescheduleEmail') || 'Envoyer l’e-mail de modification')}
                      </button>
                    )}
                    {canCancelAppointments && isCancellableAppointment(eventContextMenu.event) && (
                      <button
                        type="button"
                        onClick={() => openCancelAppointment(eventContextMenu.event)}
                        className="w-full text-left px-3 py-2 text-sm text-red-600 rounded-lg hover:bg-red-50"
                      >
                        {t('planning.calendar.contextMenu.cancelAppointment')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDuplicateEvent(eventContextMenu.event)}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      {t('planning.calendar.contextMenu.duplicate') || 'Dupliquer'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteEvent(eventContextMenu.event)}
                      className="w-full text-left px-3 py-2 text-sm text-red-600 rounded-lg hover:bg-red-50"
                    >
                      {t('planning.calendar.contextMenu.delete') || 'Supprimer'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
